// The app's own layer, over the generated store.
//
// `src/store.js` is regenerated by `appfactory product apply`, so nothing that
// took thought lives there. This file holds the thinking: which lines are
// watched, what state each one is in right now, and the notify-once rule.
//
// One deliberate divergence from the generated store: `line` records are NOT
// persisted. The metropolitan network is reference data — seventeen fixed rows
// that ship with the app — and writing it into IndexedDB would put it on the
// same id sequence as the rows people actually create, for no gain. A
// subscription therefore stores a PTV `routeId`, and `lineFor()` resolves it.

import { holdReloads } from './reload-guard.js';
import { isBusy } from './busy.js';
import { isNetworkNotice } from './notices.js';
import { computed, ref, watch } from 'vue';
import { isDemo } from './core/demo.js';
import { sharePending } from './share-link.js';
import { sampleData } from './demo.js';
import { prefs } from './core/storage.js';
import { liveryFor } from './liveries.js';
import {
  METRO_LINES, MELBOURNE_LINES, REGIONAL_LINES, isRegionalRoute,
  fetchDisruptions, usingSampleData,
} from './ptv.js';
import {
  CITIES, CITY_IDS, DEFAULT_CITY, city as cityOf, currentCity, isCity, nearestCity, setCity,
} from './city.js';
import { knownRouteIds, lineName, lineRecord, stationById } from './network.js';
import { loadNetwork } from './network-data.js';
// Modes with stops of their own (Melbourne's trams). Their own modules, so
// that a phone with a mode off runs none of it: see the header of
// src/mode-state.js and the registry in src/modes.js.
import {
  adapterModes, isAdapterLine, modeOfLine, modesOf,
} from './modes.js';
import { loadModeRoutes, modeLineRow, modeLineRows } from './mode-routes.js';
import {
  clearModeOpens, fetchModeDisruptions, markModeIntroSeen, modeOn, modeStars, writeModeOn,
} from './mode-state.js';
// The change-margin pref, spelled where the planner reads it. One-way:
// journey.js knows nothing about this file.
import { CHANGE_MARGIN_KEY, CHANGE_MARGINS } from './journey.js';
// Home, work, and the walk. Pure and import-free for the same reason
// `src/rules.js` is: the reroute engine reads it with no browser anywhere.
import {
  placesKey, TRAVEL_KEY, WALK_RADII, readPlaces, readTravel,
} from './places.js';
// The rules of the chosen set. Pure and import-free for the same reason: it
// reads rows and hands back rows, and knows nothing about a store or a ref.
import {
  chosenKey, collapseToChosen, defaultAfterRemoving, defaultRow,
} from './starred.js';
// The one sentence a push carries. Pure and graph-only: it is computed when a
// subscription is written, which may be 3am, so it must not read a live board.
import { fallbackHeadline } from './reroute.js';
import { LocalNotifications } from '@capacitor/local-notifications';
import { API_BASE, isNative, plugin } from './core/native.js';
import { ALERTABLE, severityOf, sortBySeverity, statusFromRows } from './severity.js';
// Pure, import-free, and the IDENTICAL file `server/push.mjs` runs. Everything
// about whether a notice is worth a buzz is decided there and nowhere else.
import {
  inAnyQuietWindow, shouldNotify, touches, muteForAnHour, muteForToday, muteEndFor, unmute,
} from './rules.js';
import { stepAt, welcomeBegun } from './welcome.js';
import { MAX_FAVOURITES, cleanFavourite, tripKey } from './trips.js';
import {
  buildSubscribeBody, debounced, isServerToken, postSubscribe, postUnsubscribe,
  postMute, postUnmute, muteStateFrom, muteHolds,
  pushedDisruptionIds, serverOwnsAlerts, localMayAlert, isNotEntitled,
} from './push.js';
import {
  billing, billingAvailable, billingKnown, entitled, mayAlert, currentProof, entitlementRefused,
  requireEntitlement,
} from './billing.js';
// The generated store, under its OWN names. Nothing outside this file reads
// these: they are EVERY city's rows in one list, and a screen that read one of
// them directly would show Melbourne's saved station on a Sydney board. The
// per-city views below are what the rest of the app is given.
import {
  subscriptions as allSubscriptions, subscriptionsReady,
  addSubscription as addSubscriptionRow,
  saveSubscription as saveSubscriptionRow,
  removeSubscription,
  disruptions as allDisruptions, disruptionsReady,
  addDisruption as addDisruptionRow,
  saveDisruption as saveDisruptionRow,
  removeDisruption,
  devices, devicesReady, addDevice, saveDevice,
  stations as allStations, stationsReady,
  addStation as addStationRow,
  saveStation as saveStationRow,
  removeStation,
} from './store.js';
// The trip somebody is planning is page state with a stop id in it, not saved
// data — see the note in `switchCity`.
import { EMPTY_TRIP, clearAvoid, setTrip } from './directions-state.js';
import { couldntReach } from './source-copy.js';
import { wallClock } from './zone-time.js';
// The scenario table and the row builder for "simulate a disruption". Pure,
// and it stays pure: everything reactive about a simulation is below.
import {
  rowsFor, scenariosFor, scenarioById, SIMULATION_MS, simulationExpired,
} from './simulate.js';

// Re-exported so screens have ONE import for the app's data. A page that
// reaches past this file into the generated store is a page that breaks the
// next time the product plan is applied.
export { METRO_LINES, MELBOURNE_LINES, REGIONAL_LINES, isRegionalRoute, usingSampleData };
// The hydration promises are the store's own and say nothing about a city: a
// screen awaits them to know the disk has answered, and then reads a view.
export { subscriptionsReady, disruptionsReady, stationsReady, devicesReady };
export { removeSubscription, removeDisruption, removeStation };

// ── Which city ───────────────────────────────────────────────────────────────
//
// `src/city.js` holds the answer; this holds the REACTIVE copy, because a Vue
// template cannot watch a module-level variable. Everything on a screen reads
// this ref, everything below the screens reads `currentCity()`, and `switchCity`
// is the one place that moves them together.

/** The city in force, as something a template can depend on. */
export const city = ref(currentCity());

/** Its name, operator, source and licence — see `CITIES` in city.js. */
export const cityInfo = computed(() => cityOf(city.value));

export { CITIES, CITY_IDS, nearestCity };

// ── One city's rows ──────────────────────────────────────────────────────────
//
// Every city keeps its own lines, stations, rules, destinations and ledger, and
// a switch swaps the set in rather than throwing the old one away. The rows all
// live in the SAME store — `src/store.js` is generated and has one list per
// entity — so the partition is a `city` field on the row and a view over it.
//
// ONE VIEW PER ENTITY, and every screen reads the view. The alternative was
// each screen filtering for itself, which works until the one that forgets —
// and the symptom of forgetting is not a crash but Sydney's board opening on
// Carnegie, because stop ids are globally unique and a wrong one simply matches
// nothing.
//
// THE MIGRATION IS SILENT AND HAS ONE RULE: a row with no `city` is
// Melbourne's. That is true by construction — Melbourne is the app that existed
// before there was a second city, so every row already on a phone is
// Melbourne's — and it costs nothing to read. The field is written back the
// next time that row is saved, through the writers below, so an install
// converts itself as it is used rather than in a migration pass that has to be
// right first time.
//
// The views are WRITABLE, and the setter means "these are the current city's
// rows": it replaces this city's slice and leaves every other city's alone.
// That is what lets the demo seed and the suite assign a whole list without
// either of them having to know the partition exists.

/** Which city a row belongs to. No field means Melbourne — see above. */
const cityOfRow = (row) => (isCity(row?.city) ? String(row.city) : DEFAULT_CITY);

function forCity(all) {
  return computed({
    get: () => all.value.filter((row) => cityOfRow(row) === city.value),
    set: (rows) => {
      const mine = (Array.isArray(rows) ? rows : []).map((row) => ({ ...row, city: city.value }));
      all.value = [...all.value.filter((row) => cityOfRow(row) !== city.value), ...mine];
    },
  });
}

/** The stations saved in the city in force. */
export const stations = forCity(allStations);

/** The lines watched in the city in force. */
export const subscriptions = forCity(allSubscriptions);

/**
 * The STORED ledger of the city in force — the rows a real feed put there.
 *
 * Not exported. Everything on a screen reads `disruptions` below, which is this
 * plus whatever is being simulated; the three places that must see only what
 * PTV actually said — the reconcile, the sent stamp, and the push receipt —
 * read this one by name, and they are the only ones.
 */
const storedDisruptions = forCity(allDisruptions);

/**
 * The ledger every screen reads: what PTV said, plus what is being simulated.
 *
 * ONE MERGE, HERE, AND NO SPECIAL CASES ANYWHERE ELSE. That is the whole
 * design of the simulator: the Alerts tab, the ledger colours, the line strip,
 * the reroute card, the options screen and the rule matching all go on reading
 * `disruptions` and cannot tell the difference — which is precisely what makes
 * the simulation worth looking at.
 *
 * The setter drops simulated rows on the floor. A caller assigning the ledger
 * means "these are the rows", and writing a simulated row into the store
 * through that door is the one way these could reach the disk.
 */
export const disruptions = computed({
  get: () => (simulatedRows.value.length
    ? [...storedDisruptions.value, ...simulatedRows.value]
    : storedDisruptions.value),
  set: (rows) => {
    storedDisruptions.value = (Array.isArray(rows) ? rows : []).filter((row) => !row?.simulated);
  },
});

// ── Simulating a disruption ──────────────────────────────────────────────────
//
// A button in Settings puts a recognisable failure on the ledger — a trespasser
// at Oakleigh, a track fire at South Yarra — so that the screens that only
// exist when something has gone wrong can be looked at on an ordinary Tuesday.
// `src/simulate.js` holds the scenarios and builds the rows; this holds the
// four things a screen needs and the four promises the feature rests on.
//
//   NOTHING IS STORED. The rows live in this ref and nowhere else. They are
//   never handed to `addDisruption` or `saveDisruption`, so the disruption
//   history on the disk is exactly what PTV said and nothing more.
//
//   NOTHING IS SENT. The subscribe body carries lines, stations, quiet windows
//   and rules — never disruptions — and the two paths that stamp a row against
//   the notify-once ledger read `storedDisruptions` by name. A simulated row
//   arrives at the server by no route at all.
//
//   A REAL REFRESH DOES NOT WIPE IT. The reconcile runs over the stored rows,
//   so a poll ninety seconds into a simulation retires nothing.
//
//   IT STOPS ON ITS OWN. An hour, by a timer while the app is open and by a
//   check of the stamp when it is reopened — because the timer does not
//   survive the app being killed and the stamp does.

const SIMULATION = 'lateagain:simulation:v1';

/** Per city, and Melbourne keeps the bare key — exactly as the rules do. */
const simulationKey = (id = city.value) => (id === DEFAULT_CITY ? SIMULATION : `${SIMULATION}:${id}`);

export { SIMULATION_MS };

/** `{ id, startedAt }` while something is being simulated, else null. */
const simulation = ref(null);

/** The rows it produces. Read by the merged `disruptions` view above. */
const simulatedRows = ref([]);

let simulationTimer = null;

/** What is stored for this city, if it has not already run its hour out. */
function readSimulation() {
  const stored = prefs[simulationKey()];
  const id = String(stored?.id ?? '');
  const startedAt = String(stored?.startedAt ?? '');
  if (!id || simulationExpired(startedAt)) return null;
  // A scenario that no longer resolves — a renamed station, a regenerated
  // bundle — is not an error worth showing anybody. It simply is not running.
  return scenarioById(id, city.value) ? { id, startedAt } : null;
}

/**
 * Make this the simulation in force: rows, timer, and the stored stamp.
 *
 * One function for start, stop, expiry and a city switch, because the four
 * differ only in what they pass. Four code paths writing three pieces of state
 * is how one of them ends up leaving the rows behind and the banner up.
 */
function applySimulation(entry) {
  if (simulationTimer) {
    clearTimeout(simulationTimer);
    simulationTimer = null;
  }

  simulation.value = entry;
  simulatedRows.value = entry
    ? rowsFor(entry.id, { now: Date.parse(entry.startedAt) })
    : [];

  if (entry) {
    prefs[simulationKey()] = { id: entry.id, startedAt: entry.startedAt };
    const left = Date.parse(entry.startedAt) + SIMULATION_MS - Date.now();
    // `setTimeout` rather than a poll: there is exactly one moment this has to
    // happen at, and a phone left on a desk should not be asked a question
    // every minute to find out that it is not that moment yet.
    simulationTimer = setTimeout(() => { applySimulation(null); }, Math.max(0, left));
  } else {
    delete prefs[simulationKey()];
  }
}

/** Is a simulation running? The banner and the Settings row read this. */
export const simulating = computed(() => simulation.value !== null);

/** Which one, by id. */
export const simulationId = computed(() => simulation.value?.id ?? null);

/** And by name, for the banner and the row that stops it. */
export const simulationName = computed(() => (simulation.value
  ? scenarioById(simulation.value.id, city.value)?.name ?? null
  : null));

/** Every scenario this city can be shown, as `{ id, name, scope }`. */
export const simulationScenarios = computed(() => {
  // `city` is read so the list is rebuilt on a switch: `scenariosFor` resolves
  // against the bundle in force, which this ref is the reactive shadow of.
  const id = city.value;
  return scenariosFor(id, { modes: shownModes.value.map((m) => m.id) });
});

/** Start one. Starting a second replaces the first rather than stacking. */
export function startSimulation(id) {
  const scenario = scenarioById(String(id), city.value);
  if (!scenario) return false;
  applySimulation({ id: scenario.id, startedAt: new Date().toISOString() });
  return simulatedRows.value.length > 0;
}

/** Stop. Every simulated row goes; every real one stays exactly as it was. */
export function stopSimulation() {
  applySimulation(null);
}

// Pick up whatever was running when the app was last open. Synchronous, and
// before anything reads the ledger: `prefs` is localStorage behind a proxy, and
// a banner that appears a tick after the first paint is a banner somebody has
// already scrolled past.
applySimulation(readSimulation());

// The writers, and there is nothing to them but the stamp. A new row is the
// current city's; an edited row keeps the city it already had, which is how a
// legacy row is quietly written back as Melbourne's the first time it is
// touched. Two calls for a new row because `addStationRow` builds its record
// from the fields the product plan declares and would drop `city` — the same
// reason `refresh()` has always written a disruption twice.

export function addStation(input = {}) {
  return saveStationRow({ ...addStationRow(input), city: city.value });
}

export function saveStation(row) {
  return saveStationRow({ ...row, city: cityOfRow(row) });
}

export function addSubscription(input = {}) {
  return saveSubscriptionRow({ ...addSubscriptionRow(input), city: city.value });
}

export function saveSubscription(row) {
  return saveSubscriptionRow({ ...row, city: cityOfRow(row) });
}

export function addDisruption(input = {}) {
  return saveDisruptionRow({ ...addDisruptionRow(input), city: city.value });
}

export function saveDisruption(row) {
  return saveDisruptionRow({ ...row, city: cityOfRow(row) });
}

// THE DEVICE IS NOT PARTITIONED, and that is the point of the line. There is
// one phone, it has one registration token, and the server keeps one
// subscription per device: a per-city device would be this app claiming to be
// two installs. Which city it is subscribed FOR travels in the subscribe body.

/**
 * Every line of the city in force, in the order the network map groups them.
 *
 * MELBOURNE'S COLOURS AND ORDER STAY IN `METRO_LINES`, and that is deliberate
 * rather than an oversight. The Melbourne bundle is generated by
 * `scripts/fetch-network.mjs` from PTV's own endpoints and carries no colour at
 * all, while `METRO_LINES` carries both the palette the whole design rests on
 * and the ORDER the network map groups the lines in — dark blue, yellow, green,
 * light blue, orange, red, grey. Sorting that list by route id, which is what
 * deriving it from the bundle would do, would scatter the palette across the
 * one screen where it reads as a palette.
 *
 * The Sydney bundle carries `colour` and `short` per the contract, so it needs
 * no second table — its own key order is T1 upward, which is the order anybody
 * would list them in.
 *
 * Names come from the bundle either way: it is the thing a script refreshes.
 */
export const lineList = computed(() => {
  if (city.value === 'melbourne') {
    // METROPOLITAN THEN REGIONAL, in one list, because a V/Line line is a line:
    // it has a name, a colour, a stop list, disruptions, a board and a
    // subscription. The `regional` flag rides along for the two screens that
    // have to tell the kinds apart — the Lines tab's own heading, and the trip
    // planner, which routes onto V/Line now but pays a regional leg a cost in
    // its ranking and will not quote a myki fare for one.
    //
    // THEN EACH SHOWN MODE'S ROUTES (Melbourne's trams), and only for a phone
    // that has turned them on. Off, this is the list it has always been, row
    // for row.
    const trains = MELBOURNE_LINES.map((l) => ({ ...l, name: lineName(l.routeId) ?? l.name }));
    const extra = shownModes.value.flatMap((m) => modeLineRows(m));
    return extra.length ? [...trains, ...extra] : trains;
  }
  return knownRouteIds()
    .sort((a, b) => a - b)
    .map((routeId) => {
      const row = lineRecord(routeId) ?? {};
      return {
        routeId,
        name: row.name ?? `Line ${routeId}`,
        short: row.short ?? '',
        colour: row.colour ?? UNKNOWN_LINE_COLOUR,
        // What kind of thing it is: 'train' unless the bundle says otherwise,
        // which it does for the light rail lines in Sydney, Brisbane, Adelaide
        // and Canberra. The only screen that reads it is the Lines tab, which
        // groups the trams under their own heading rather than filing four of
        // them among the trains as if a tram were a train. Melbourne's bundle
        // carries no modes at all and every line there is a train.
        mode: row.mode ?? 'train',
        // A regional service, which Perth's bundle marks the way Melbourne's
        // table does: the Lines tab files them under REGIONAL and the ledger
        // sorts them after the suburban lines.
        regional: Boolean(row.regional),
      };
    });
});

/** The old name, kept because three screens and the ledger say `lines`. */
export const lines = lineList;

/** The grey a line with no colour of its own is drawn in. */
const UNKNOWN_LINE_COLOUR = '#909194';

/** A line record from a route id. Never returns undefined into a template. */
export function lineFor(routeId) {
  return lineList.value.find((l) => Number(l.routeId) === Number(routeId))
    // A mode's route is still its route with the mode switched off: the Alerts
    // history keeps what it was told about route 96 for a week, and "Unknown
    // line" over it would be the history forgetting.
    ?? lineOfAnyMode(routeId)
    ?? { routeId, name: 'Unknown line', colour: UNKNOWN_LINE_COLOUR };
}

/** The row for a line id in any declared adapter mode's band, on or off, or null. */
function lineOfAnyMode(routeId) {
  const mode = modeOfLine(routeId);
  return mode ? modeLineRow(mode, routeId) : null;
}

/**
 * The modes the city in force is showing: its adapter modes that are on. An
 * opt-in mode is on only when this phone has said so; one that is not opt-in is
 * always on. Empty for a train-only phone, which is what keeps it unchanged.
 */
export const shownModes = computed(() => adapterModes(city.value).filter((m) => modeOn(m)));

/** Is this mode showing in the city in force? */
export const modeShown = (mode) => shownModes.value.some((m) => m === mode);

/** Does the city in force have any opt-in mode to offer? */
export const offeredModes = computed(() => modesOf(city.value).filter((m) => m.optIn));

// The route tables, loaded when they are wanted and never otherwise: a phone
// with a mode on, at launch or the moment it is turned on. Everything that
// reads them is reactive on them, so the rows fill in when they land.
watch(shownModes, (list) => { for (const m of list) loadModeRoutes(m); }, { immediate: true });

/**
 * Turn a mode on or off for this phone.
 *
 * OFF TAKES THE MODE'S ROUTES OFF THE WATCHED LIST. A route nobody can see on
 * the ledger must not be able to wake anybody at 6am, and keeping it watched
 * behind a hidden switch would be exactly that. Their rows go from the ledger on
 * the next poll the same way any notice no longer in the feed does.
 *
 * ON loads the stops in the background, so the first board opened does not
 * wait for them, and polls straight away so the routes chosen next have their
 * status by the time they are on the ledger.
 */
export function setModeOn(mode, on) {
  writeModeOn(mode, on);
  if (!on) {
    for (const sub of [...subscriptions.value]) {
      if (modeOfLine(sub.line) === mode) removeSubscription(sub.id);
    }
  } else {
    // The stops, in the background, so the first screen of the mode does not
    // wait for them. Imported here rather than at the top: a phone without the
    // mode never loads them at all.
    import('./mode-stops.js').then((m) => m.loadModeStops(mode)).catch(() => {});
  }
  refresh();
}

/**
 * Change city. A plain toggle: nothing is cleared, and nothing is copied.
 *
 * THIS USED TO BE THE MOST EXPENSIVE CONTROL IN THE APP. Switching threw away
 * the watched lines, the saved stations, the rules, the destinations and the
 * ledger — every one of them an id that means nothing in the other network —
 * and the Settings row had to ask SWITCH or KEEP before it happened. The
 * reasoning was sound and the conclusion was wrong: those things do not have
 * to be DESTROYED to stop them showing up in the other city, they only have to
 * be filed under the city they belong to. So they are, and a switch is now the
 * cheapest control on the screen.
 *
 * WHAT MOVES: the rows, through the views above, which follow `city` on their
 * own — so there is nothing here to re-point. The rules, the recent
 * destinations and the board's remembered destinations live in `prefs` under a
 * key that carries the city, so the rules list is re-read and the other two are
 * read per call. Melbourne keeps the bare keys it has always had, which is what
 * makes an existing install migrate by doing nothing.
 *
 * WHAT IS SHARED, because it is about the person rather than a railway: the
 * quiet windows, the mute, whether alerts are on at all, and the device. One
 * phone is one registration; somebody who moves between two cities still does
 * not want to be woken at 3am in either.
 *
 * WHAT IS SEEDED, once: a city nobody has saved a station in gets its own
 * default, so the board opens on something rather than on the picker. That is
 * the first-run state for that city and not a consequence of the switch — come
 * back to a city you have used and your own stations are exactly as you left
 * them.
 *
 * WHAT IS DROPPED: the planned trip and what it was told to avoid. Module refs
 * that are deliberately NOT persisted — "a plan is a statement about the next
 * hour" — and their ids are stop and route ids in the bundle being left, so a
 * Sydney board would plan around a Melbourne line id that means nothing there.
 * That is the same category as the Trains tab's own reset: page state, not
 * saved data.
 *
 * The subscribe is re-sent so the sweep alerts this phone about the city it is
 * showing, and it is awaited — that is what makes the caller able to say when
 * the switch is finished. The refresh is not: it is a poll, it reports its own
 * failures on screen, and a switch that waited for a train feed would be a
 * toggle that visibly lags.
 */
export async function switchCity(id) {
  if (!isCity(id) || String(id) === city.value) return city.value;

  // The city's network, in memory BEFORE it is in force: everything below reads
  // it synchronously, and a switch that landed first would read an empty city.
  await loadNetwork(id);
  if (String(id) === city.value) return city.value;

  setCity(id);
  city.value = currentCity();

  // The rules of the city now in force. `rules` is a ref over a per-city
  // `prefs` key, so this is a re-read rather than a conversion.
  rules.value = readRules();
  favourites.value = readFavourites();

  // And this city's home and work. Same shape, same reason: a stop id is a
  // Melbourne stop id, and the reroute engine reading Carnegie while Sydney is
  // on screen would plan a way home to a station that is not in the bundle.
  places.value = readPlaces(prefs[placesKey(city.value)]);

  // A board to open on, for a city this phone has never shown before.
  if (!stations.value.length) {
    const home = stationById(cityOf(city.value).defaultStopId);
    if (home) {
      addStation({
        name: home.name,
        stopId: home.stopId,
        suburb: home.suburb ?? '',
        lines: (home.lines ?? []).join(','),
        isDefault: true,
      });
    }
  }

  // The one-time tidy, for the city being opened rather than for the app. See
  // `collapseToChosen`: a phone that used Melbourne for a year and opens
  // Sydney today has two histories to collapse and they happen on two days.
  collapseChosen();

  // And the simulation, which is per city for the same reason the rules are:
  // its rows name this city's lines, and carrying them across a switch would
  // put a Melbourne suspension on a Sydney ledger.
  applySimulation(readSimulation());

  setTrip({ ...EMPTY_TRIP });
  clearAvoid();

  await subscribeNow();
  // Not awaited: see above.
  refresh();
  return city.value;
}

// ── What a line's state is right now ─────────────────────────────────────────

// The severity model itself lives in `src/severity.js` — pure, importing
// nothing, so that `server/push.mjs` can run the IDENTICAL code when it decides
// whether to wake a phone. A second copy server-side would drift, and the
// symptom would be a 6am notification about a car park.
//
// Re-exported here so every screen still has one import for the app's data.
export {
  delayMinutesFrom, severityOf, PLANNED_HORIZON_MS, SEVERITY_RANK, ALERTABLE,
  replacementOf, labelFor, statusWord, cancellationNote,
} from './severity.js';

/**
 * Disruptions on a line, worst first, then newest.
 *
 * NETWORK NOTICES ARE NOT A LINE'S, and are left out here. PTV files the Royal
 * Show notice against every metro line it has, so reading it as a line's own
 * put it on sixteen line screens and under "ALSO ON THIS LINE" on each — and
 * opened from the Alerts card it wore whichever line happened to be stored
 * first, which is how a notice about the Showgrounds came to read as a Werribee
 * alert. It has one place, the network card on Alerts (src/notices.js).
 */
export function disruptionsFor(routeId, now = Date.now()) {
  return sortBySeverity(
    disruptions.value.filter((d) =>
      Number(d.line) === Number(routeId) && !d.clearedAt && !isNetworkNotice(d, now)),
    now,
  );
}

/** Every line one PTV notice is filed against, in the order they arrived. */
export function linesOfNotice(ptvId) {
  const ids = disruptions.value
    .filter((d) => Number(d.ptvId) === Number(ptvId) && !d.clearedAt)
    .map((d) => Number(d.line));
  return [...new Set(ids)];
}

/**
 * One line's status, from the disruptions cached against it.
 *
 * The rows arrive worst-first, so the answer is the first one — which is how
 * "Delays up to 15 minutes due to a train fault in the Craigieburn area" beats
 * three newer station notices and the row finally reads `+15 MIN`.
 */
export function statusFor(routeId, now = Date.now()) {
  return statusFromRows(disruptionsFor(routeId, now), now);
}

// ── The ledger ───────────────────────────────────────────────────────────────

/**
 * The home screen's rows. Shape matches `LedgerLine` in LineLedger.vue.
 *
 * `id` is the SUBSCRIPTION id, not the route id, because that is what a tap
 * has to be able to act on. The disruption a row leads to rides along as
 * `disruptionId` so Home does not have to recompute the status to navigate.
 */
export const ledger = computed(() =>
  subscriptions.value
    .filter((s) => s.active !== false)
    .map((s) => {
      const line = lineFor(s.line);
      const state = statusFor(s.line);
      return {
        id: String(s.id),
        routeId: line.routeId,
        name: line.name,
        colour: line.colour,
        // A row of a mode with stops of its own draws its number on a badge and
        // its two ends beside it; a train row carries none of these and draws
        // as it did.
        ...(isAdapterLine(line.routeId) ? { mode: line.mode, short: line.short, ends: line.ends } : {}),
        status: state.status,
        delayMinutes: state.delayMinutes,
        note: state.note,
        replacement: state.replacement ?? null,
        disruptionId: state.top ? String(state.top.id) : null,
      };
    })
    // Within a status, the trains and then the modes — the order the person
    // added them in, near enough, and the order the network map would.
    .sort((a, b) => RANK[a.status] - RANK[b.status]
      || Number(isAdapterLine(a.routeId)) - Number(isAdapterLine(b.routeId))
      || a.name.localeCompare(b.name)));

// Trouble first. Someone opening this app at 6:40am is looking for the one bad
// row, and making them read past four good ones to find it is the whole
// failure this app exists to prevent.
const RANK = { suspended: 0, delayed: 1, planned: 2, good: 3 };

/** Route ids currently watched, for the Choose lines checkboxes. */
export const watchedRouteIds = computed(
  () => new Set(subscriptions.value.map((s) => Number(s.line))),
);

/**
 * Replace the watched set with exactly these route ids.
 *
 * Additions inherit the quiet hours already in force, so someone who set
 * 21:00–06:00 last month does not get woken by a line they added today.
 */
export function saveWatchedLines(routeIds, { modes = [] } = {}) {
  const wanted = new Set(routeIds.map(Number));
  const settings = notificationSettings.value;

  for (const sub of [...subscriptions.value]) {
    // A mode's routes are that mode's own question. The welcome's station step
    // and anything else that only knows about trains leaves them exactly as
    // they were; a caller that shows a mode names it in `modes`.
    const owner = modeOfLine(sub.line);
    if (owner && !modes.includes(owner)) continue;
    if (!wanted.has(Number(sub.line))) removeSubscription(sub.id);
  }
  const have = new Set(subscriptions.value.map((s) => Number(s.line)));
  for (const routeId of wanted) {
    if (have.has(routeId)) continue;
    addSubscription({
      line: routeId,
      active: true,
      quietFrom: settings.quietFrom,
      quietUntil: settings.quietUntil,
      weekdaysOnly: settings.weekdaysOnly,
    });
  }
}

/**
 * Replace the watched routes of ONE mode with exactly these, leaving every other
 * line as it is. The welcome's "Any trams?" step and the card's ADD TRAMS are the
 * callers; choosing at least one turns the mode on, because a watched route on a
 * phone with the mode off would be a route nobody can see.
 */
export function saveWatchedModeLines(mode, routeIds) {
  const mine = routeIds.map(Number).filter((id) => modeOfLine(id) === mode);
  const others = subscriptions.value.map((s) => Number(s.line)).filter((id) => modeOfLine(id) !== mode);
  if (mine.length && !modeOn(mode)) writeModeOn(mode, true);
  saveWatchedLines([...others, ...mine], { modes: [mode] });
}

/**
 * Watch or stop watching ONE route of a mode, leaving every other line as it
 * is. The route screen's own toggle: the same act as ticking the route on Choose
 * lines, without the trip to that screen. Watching turns the mode on, as every
 * other way of choosing a route does.
 */
export function setModeRouteWatched(mode, routeId, on) {
  const id = Number(routeId);
  if (modeOfLine(id) !== mode) return;
  const held = subscriptions.value.map((s) => Number(s.line)).filter((x) => modeOfLine(x) === mode);
  saveWatchedModeLines(mode, on ? [...new Set([...held, id])] : held.filter((x) => x !== id));
}

/**
 * The answer to the welcome's "Any <mode>?", either way, in the one tap that
 * leaves the step. Routes chosen are watched and turn the mode on; "No trams"
 * (none chosen) leaves it off and drops any routes a re-run welcome had. Both
 * answer the "You can add trams now" card for good, so somebody who said no is
 * never asked again; Settings is where a change of mind goes.
 */
export function answerModeStep(mode, routeIds) {
  markModeIntroSeen(mode);
  const ids = [...routeIds].map(Number).filter((id) => modeOfLine(id) === mode);
  if (ids.length) saveWatchedModeLines(mode, ids);
  else if (modeOn(mode)) setModeOn(mode, false);
}

// ── Quiet windows ────────────────────────────────────────────────────────────
//
// The absolute mute, and there can now be several. One overnight window was
// too blunt for the two people this app is actually for: the shift worker who
// sleeps 09:00–16:00, and the commuter who wants nothing on a Sunday. Both
// were expressible only by giving up the other.
//
// `prefs['lateagain:quiet:v1']` is the source of truth:
//
//   [{ id: 'q-legacy', from: '22:00', to: '06:00', days: [0,1,2,3,4,5,6] }]
//
// `days` is in JS `getDay()` order — 0 is Sunday — because that is the order
// `src/rules.js` reads, and converting between two orders at the boundary is
// how one of them ends up off by one.
//
// localStorage rather than a store record, for the same reason the first-run
// flag is: it is read SYNCHRONOUSLY, and `isQuiet()` is asked the question
// during the launch window, before IndexedDB has answered anything.

const QUIET_WINDOWS = 'lateagain:quiet:v1';

const WEEKDAY_NUMBERS = [1, 2, 3, 4, 5];
const EVERY_DAY = [0, 1, 2, 3, 4, 5, 6];

/** Ids only have to be unique on this phone, and readable in a log. */
let idSeed = 0;
const newId = (prefix) => `${prefix}-${Date.now().toString(36)}-${(idSeed += 1).toString(36)}`;

/** Minutes EAST of UTC for an instant, which is the sign `src/rules.js` wants. */
const tzOffsetFor = (at = Date.now()) => -(at instanceof Date ? at : new Date(at)).getTimezoneOffset();

function cleanDays(days) {
  return [...new Set((Array.isArray(days) ? days : [])
    .map(Number)
    .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6))]
    .sort((a, b) => a - b);
}

function cleanWindow(row, index = 0) {
  if (!row) return null;
  return {
    id: String(row.id ?? `q-${index}`),
    from: String(row.from ?? '').trim(),
    to: String(row.to ?? '').trim(),
    days: cleanDays(row.days),
  };
}

export const quietWindows = ref(readWindows());

function readWindows() {
  const stored = prefs[QUIET_WINDOWS];
  return Array.isArray(stored) ? stored.map(cleanWindow).filter(Boolean) : [];
}

// Demo mode shares an origin with the real app in development, and losing your
// quiet hours to a stray /demo visit would be unforgivable. It cannot happen:
// `prefs` is backed by memory under /demo — see src/core/storage.js — so this
// writes, as it must (the city switch re-reads what was written), and the
// write is gone on reload along with everything else the demo did.
function writeWindows() {
  prefs[QUIET_WINDOWS] = quietWindows.value;
}

/**
 * The windows a legacy `notificationSettings` object means.
 *
 * ONE window, or none. Three shapes go in:
 *
 *   22:00–06:00, weekdaysOnly false  → one window, every day
 *   22:00–06:00, weekdaysOnly true   → one window, Monday to Friday
 *   an empty pair                    → no window at all
 *
 * WHAT THIS DROPS, deliberately: `weekdaysOnly` used to mean "and the whole
 * weekend is quiet as well", which is a different sentence from "the overnight
 * window applies on weeknights". Weekends-off is now said on the RULE that
 * describes the trip — `days: [1,2,3,4,5]` — where it belongs, because it is a
 * fact about the commute and not about sleep. Somebody who had the old switch
 * on and wants the old behaviour adds a second window across the weekend, and
 * can now see that they have.
 */
export function quietWindowsFromSettings(settings) {
  const from = String(settings?.quietFrom ?? '').trim();
  const to = String(settings?.quietUntil ?? '').trim();
  const out = [];
  // The overnight pair ran on EVERY day; `weekdaysOnly` never narrowed it.
  if (from && to) out.push({ id: 'q-legacy', from, to, days: [...EVERY_DAY] });
  // What `weekdaysOnly` actually meant: the whole weekend is quiet as well —
  // the old isQuiet answered true for any Saturday or Sunday before it even
  // looked at the window, cleared or not. Two halves, because a window's end
  // is exclusive and one window cannot say a whole day. This is the same
  // conversion `legacyQuietWindows` makes in server/push.mjs; the two must
  // agree or a phone and the server would disagree about a Saturday.
  if (settings?.weekdaysOnly) {
    out.push({ id: 'q-legacy-weekend-am', from: '00:00', to: '12:00', days: [0, 6] });
    out.push({ id: 'q-legacy-weekend-pm', from: '12:00', to: '00:00', days: [0, 6] });
  }
  return out;
}

/**
 * Is `at` inside any quiet window?
 *
 * A thin adapter over `inAnyQuietWindow`, which is the one implementation —
 * `server/push.mjs` runs the identical function against the identical windows,
 * and a second copy here would drift within a month.
 *
 * Called with no argument it asks about THIS phone, now. Called with a legacy
 * settings object it asks about the windows that object stands for, which is
 * what keeps the one remaining caller of the old shape honest.
 */
export function isQuiet(settings = null, at = new Date()) {
  const windows = settings ? quietWindowsFromSettings(settings) : quietWindows.value;
  const when = at instanceof Date ? at : new Date(Number(at));
  return inAnyQuietWindow(windows, when.getTime(), tzOffsetFor(when));
}

// ── Network notices ──────────────────────────────────────────────────────────
//
// Whether this phone is pushed PTV's network notices — special event trains,
// holiday timetables — as well as faults on its lines. See src/notices.js.
// Off by default: they are news, not faults, and a person who installed this
// to hear about their own line did not ask for the Royal Show.

const NOTICE_PUSHES_KEY = 'lateagain:notice-pushes:v1';

export const noticePushes = ref(prefs[NOTICE_PUSHES_KEY] === true);

/** Turn network-notice pushes on or off, and tell the server. */
export function setNoticePushes(on) {
  noticePushes.value = on === true;
  prefs[NOTICE_PUSHES_KEY] = noticePushes.value;
}

/** Replace every window. Written through immediately; Settings has no Save. */
export function saveQuietWindows(list) {
  quietWindows.value = (Array.isArray(list) ? list : []).map(cleanWindow).filter(Boolean);
  writeWindows();
  return quietWindows.value;
}

/**
 * One more window, with the defaults almost everybody would type.
 *
 * Returned so the screen can open it for editing: a window added and left at
 * 22:00–06:00 because nobody noticed it needed filling in is a window that
 * silences an alert nobody asked to lose.
 */
export function addQuietWindow(patch = {}) {
  const window = cleanWindow({ id: newId('q'), from: '22:00', to: '06:00', days: EVERY_DAY, ...patch });
  saveQuietWindows([...quietWindows.value, window]);
  return window;
}

export function removeQuietWindow(id) {
  saveQuietWindows(quietWindows.value.filter((w) => String(w.id) !== String(id)));
}

const DAY_LABELS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

/**
 * `EVERY DAY`, `WEEKDAYS`, `MON WED FRI` — a row's day line.
 *
 * The same vocabulary `describeRule` in `src/rules.js` uses, and deliberately
 * the same three shortcuts: a word is read at a glance where
 * `MON TUE WED THU FRI` is read letter by letter. It is not imported from
 * there because that function describes a RULE — days and a time span
 * together — and a quiet window shows its span on the line above.
 */
export function describeDays(days) {
  const list = cleanDays(days);
  if (!list.length || list.length === 7) return 'EVERY DAY';
  if (list.length === 5 && WEEKDAY_NUMBERS.every((d) => list.includes(d))) return 'WEEKDAYS';
  if (list.length === 2 && list.includes(0) && list.includes(6)) return 'WEEKENDS';
  return list.map((d) => DAY_LABELS[d]).join(' ');
}

// ── Notification settings, as a legacy adapter ───────────────────────────────
//
// Quiet hours used to live on the SUBSCRIPTION rows, one copy per watched
// line, and Settings presented the first one for all of them. Nothing writes
// them any more — Settings edits `quietWindows` — but two readers still ask in
// the old vocabulary: the Home screen's QUIET chip, and the `quiet` object the
// subscribe body still carries for a server that predates this change. Both
// now read the FIRST quiet window, so neither can show or send a stale value.

// Nobody wants to be told about the Frankston line at 2am. Overnight quiet is
// what almost everyone would set by hand, so it is what a new install starts
// with — and deleting the window still empties it for the shift workers who
// want the opposite.
const DEFAULT_SETTINGS = { quietFrom: '20:00', quietUntil: '06:00', weekdaysOnly: false };

export const notificationSettings = computed(() => {
  const first = quietWindows.value[0];
  if (!first) return { quietFrom: '', quietUntil: '', weekdaysOnly: false };
  return {
    quietFrom: first.from,
    quietUntil: first.to,
    weekdaysOnly: describeDays(first.days) === 'WEEKDAYS',
  };
});

/** The same object, read off the rows the legacy screens wrote it to. */
const legacySettings = () => {
  const first = subscriptions.value[0];
  if (!first) return { ...DEFAULT_SETTINGS };
  return {
    quietFrom: first.quietFrom || '',
    quietUntil: first.quietUntil || '',
    weekdaysOnly: !!first.weekdaysOnly,
  };
};

/**
 * Apply the overnight default once, for installs that predate it.
 *
 * Existing subscriptions carry empty quiet hours because the app had no
 * default when they were created. Seeding them silently on every load would
 * fight anyone who cleared them on purpose, so this runs ONCE per install and
 * records that it has — after which the field is the person's, not ours.
 *
 * It still runs, and it still runs FIRST, because the migration below reads
 * what it writes: an install that never had a window would otherwise migrate
 * to no window at all, which is the one outcome nobody chose.
 */
const QUIET_DEFAULTED = 'lateagain:quiet-defaulted:v1';

function applyQuietDefaultOnce() {
  let already = false;
  try { already = localStorage.getItem(QUIET_DEFAULTED) === '1'; } catch { already = true; }
  if (already) return;

  const rows = subscriptions.value.filter((r) => !r.quietFrom && !r.quietUntil);
  for (const row of rows) {
    saveSubscription({ ...row, quietFrom: DEFAULT_SETTINGS.quietFrom, quietUntil: DEFAULT_SETTINGS.quietUntil });
  }
  try { localStorage.setItem(QUIET_DEFAULTED, '1'); } catch { /* private mode; it will try again */ }
}

/**
 * The old settings become the first window. Once, silently, and never again.
 *
 * The KEY'S PRESENCE is the record that it happened, which is why an empty
 * result is still written: somebody who deletes their last window must not
 * have it conjured back the next time the app starts.
 *
 * It waits for `subscriptionsReady` because the legacy value lives on rows
 * IndexedDB has not handed over at module scope — migrating before then would
 * read the defaults and quietly overwrite a 21:00 window somebody set by hand.
 */
/**
 * What a new install starts with.
 *
 * Two windows, not one. Nights, because nobody wants the Frankston line at
 * 2am; and the middle of the weekday, because this app is for the commute and
 * a signal fault at 1pm on a Tuesday is not news to somebody at a desk until
 * half past four. Both are visible in Settings and either goes with one tap.
 * Weekends are NOT muted by default — plenty of people ride on a Saturday —
 * and adding that window is the same one tap.
 */
export const DEFAULT_QUIET_WINDOWS = [
  { id: 'q-night', from: '20:00', to: '06:00', days: [0, 1, 2, 3, 4, 5, 6] },
  { id: 'q-workday', from: '10:00', to: '16:00', days: [1, 2, 3, 4, 5] },
];

function migrateQuietWindowsOnce() {
  if (prefs[QUIET_WINDOWS] !== undefined) return;
  // A fresh install has no lines yet and therefore no old settings to carry
  // over: it gets the defaults. An install with lines migrates what it had.
  // `saveQuietWindows` writes even an empty list, which is the point: the key
  // exists afterwards either way, and that is what makes this run once.
  const fresh = subscriptions.value.length === 0;
  saveQuietWindows(fresh
    ? DEFAULT_QUIET_WINDOWS.map((w) => ({ ...w, days: [...w.days] }))
    : quietWindowsFromSettings(legacySettings()));
}

subscriptionsReady.then(() => {
  if (isDemo.value) return;
  applyQuietDefaultOnce();
  migrateQuietWindowsOnce();
});

/** Legacy writer. Nothing in the app calls it; it edits the first window. */
export function saveNotificationSettings(patch) {
  const first = quietWindows.value[0];
  const merged = { ...notificationSettings.value, ...patch };
  const built = quietWindowsFromSettings(merged);
  const rest = quietWindows.value.slice(1);
  saveQuietWindows(built.length
    ? [{ ...built[0], id: first?.id ?? built[0].id }, ...rest]
    : rest);
}

// ── Commute rules ────────────────────────────────────────────────────────────
//
// What is worth a buzz, and when. `prefs['lateagain:rules:v1']`:
//
//   [{ id, from: 1034, to: 1234, fromName: 'Carnegie', toName: 'State Library',
//      start: '08:00', end: '09:30', lead: 30, days: [1,2,3,4,5],
//      active: true, mutedUntil: null,
//      path: { stops: [1034, …], routes: [4, 11, 14] }, resolvedAt }]
//
// The decisions made about them are NOT here: `src/rules.js` is pure, imports
// nothing, and is the identical file `server/push.mjs` runs. This section owns
// the reactive list, the disk, and the one thing the server cannot do —
// resolving a trip into the stops and lines it rides.

const RULES = 'lateagain:rules:v1';

/**
 * The rules key for a city. Melbourne keeps the bare one.
 *
 * A KEY SUFFIX RATHER THAN A `city` FIELD ON THE RULE, and the choice is made
 * by the two things that must not change. `src/rules.js` is import-free and is
 * the identical file `server/push.mjs` runs, so a rule it reads may not grow a
 * field it would have to ignore; and the subscribe body is validated by the
 * server field by field, so a `city` on a rule would 400 the whole subscribe
 * and take the watched lines down with it. A key the phone alone reads costs
 * neither.
 *
 * Melbourne's suffix is empty on purpose: `prefs['lateagain:rules:v1']` is
 * where every rule already on a phone is, and this is what makes it stay
 * Melbourne's without a migration pass.
 */
const rulesKey = (id = city.value) => (id === DEFAULT_CITY ? RULES : `${RULES}:${id}`);

/** Half an hour of warning is a train you can still make a different plan for. */
export const DEFAULT_LEAD = 30;

/** And the most the server will accept, so the two agree. */
const MAX_LEAD = 180;

/** Enough for a morning, an evening, a school run and a weekend trip, twice over. */
export const MAX_RULES = 20;

export const rules = ref(readRules());

function readRules() {
  const stored = prefs[rulesKey()];
  return Array.isArray(stored) ? stored.map(cleanRule).filter(Boolean).slice(0, MAX_RULES) : [];
}

// Writes under /demo too, into the memory-backed `prefs`. It has to: the rules
// ref is re-read from this key on every city switch, and a guard here would
// mean a demo that loses its seeded commute the moment somebody looks at
// Sydney and comes back.
function writeRules() {
  prefs[rulesKey()] = rules.value;
}

function cleanRule(row) {
  if (!row) return null;
  const lead = Number(row.lead);
  return {
    id: String(row.id ?? newId('r')),
    from: Number(row.from),
    to: Number(row.to),
    fromName: String(row.fromName ?? ''),
    toName: String(row.toName ?? ''),
    start: String(row.start ?? '').trim(),
    end: String(row.end ?? '').trim(),
    // Three hours is the server's ceiling too. A lead past it would make the
    // whole subscribe body 400, taking the watched lines down with it.
    lead: Number.isFinite(lead) ? Math.min(MAX_LEAD, Math.max(0, Math.round(lead))) : DEFAULT_LEAD,
    days: cleanDays(row.days),
    active: row.active !== false,
    mutedUntil: row.mutedUntil ?? null,
    path: cleanPath(row.path),
    resolvedAt: row.resolvedAt ?? null,
  };
}

function cleanPath(path) {
  const stops = (Array.isArray(path?.stops) ? path.stops : []).map(Number).filter(Number.isFinite);
  if (!stops.length) return null;
  const routes = (Array.isArray(path?.routes) ? path.routes : []).map(Number).filter(Number.isFinite);
  return { stops, routes };
}

function minutesOfClock(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm ?? '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/**
 * The stops and lines a trip rides, resolved on THIS phone.
 *
 * Imported dynamically, and it is not caution: `src/rule-path.js` reaches the
 * journey planner and the bundled network behind it — 54 KB of station data
 * that is otherwise fetched the first time somebody opens the board. A static
 * import here would move all of it into the chunk a cold open has to download,
 * to serve a screen most people visit once.
 */
async function resolveRulePath(from, to) {
  try {
    const { resolvePath } = await import('./rule-path.js');
    return resolvePath(from, to);
  } catch {
    // No network data — a test environment, or a chunk that would not load.
    // The caller refuses the save, which is the honest answer: a rule with no
    // path matches nothing and would sit on screen never firing.
    return null;
  }
}

/**
 * Write a rule, new or edited. `{ ok: true, rule }` or `{ ok: false, error }`.
 *
 * ASYNC BECAUSE THE PATH IS RESOLVED HERE, at save time and once, exactly as
 * `src/rule-path.js` describes: the server cannot resolve anything, so the
 * phone hands it the answer as plain data.
 *
 * A REFUSAL IS A SENTENCE, not a thrown error. Every one of these is something
 * the person can fix on the screen they are already looking at, and the editor
 * prints it under the Save button.
 */
export async function saveRule(input = {}) {
  const from = Number(input.from);
  const to = Number(input.to);
  if (!Number.isFinite(from) || !Number.isFinite(to)) {
    return { ok: false, error: 'Pick both ends of the trip first.' };
  }
  if (from === to) {
    return { ok: false, error: 'Those are the same station, and a trip needs two ends.' };
  }

  const start = minutesOfClock(input.start);
  const end = minutesOfClock(input.end);
  if (start === null || end === null) {
    return { ok: false, error: 'Give the window a start and an end.' };
  }
  if (end <= start) {
    return { ok: false, error: 'The window has to end after it starts.' };
  }

  const existing = rules.value.find((r) => String(r.id) === String(input.id ?? ''));
  if (!existing && rules.value.length >= MAX_RULES) {
    return { ok: false, error: `That is more than ${MAX_RULES} rules. Delete one first.` };
  }

  // Re-resolved on every save rather than only when the ends change: the
  // network is bundled, so a rule saved before the Metro Tunnel opened carries
  // a path that no longer describes the trip, and `resolvedAt` is what says so.
  const path = await resolveRulePath(from, to);
  if (!path) {
    const fromName = String(input.fromName ?? '').trim() || 'there';
    const toName = String(input.toName ?? '').trim() || 'there';
    return {
      ok: false,
      error: `We couldn’t work out a train from ${fromName} to ${toName}, so this rule would never match anything.`,
    };
  }

  const rule = cleanRule({
    ...existing,
    ...input,
    id: existing?.id ?? newId('r'),
    path,
    resolvedAt: new Date().toISOString(),
  });

  rules.value = existing
    ? rules.value.map((r) => (String(r.id) === String(rule.id) ? rule : r))
    : [...rules.value, rule];
  writeRules();
  return { ok: true, rule };
}

// ── Favourite trips ──────────────────────────────────────────────────────────
//
// Two stations and nothing else, per city, beside the rules (see src/trips.js
// for why they are two stores and one list). Writes under /demo too, for the
// reason the rules do.

const FAVOURITES = 'lateagain:favourites:v1';
const favouritesKey = (id = city.value) => (id === DEFAULT_CITY ? FAVOURITES : `${FAVOURITES}:${id}`);

function readFavourites() {
  const stored = prefs[favouritesKey()];
  return Array.isArray(stored) ? stored.map(cleanFavourite).filter(Boolean).slice(0, MAX_FAVOURITES) : [];
}

export const favourites = ref(readFavourites());

function writeFavourites() {
  prefs[favouritesKey()] = favourites.value;
}

/** Save a trip. `{ ok, error? }`; saving one already saved is a quiet yes. */
export function addFavourite(trip) {
  const fav = cleanFavourite({ ...trip, savedAt: Date.now() });
  if (!fav) return { ok: false, error: 'A trip needs two different stations.' };
  const key = tripKey(fav.from, fav.to);
  if (favourites.value.some((f) => tripKey(f.from, f.to) === key)) return { ok: true };
  if (favourites.value.length >= MAX_FAVOURITES) {
    return { ok: false, error: `That is more than ${MAX_FAVOURITES} favourites. Remove one first.` };
  }
  favourites.value = [...favourites.value, fav];
  writeFavourites();
  return { ok: true };
}

export function removeFavourite(from, to) {
  const key = tripKey(from, to);
  favourites.value = favourites.value.filter((f) => tripKey(f.from, f.to) !== key);
  writeFavourites();
}

export function isFavourite(from, to) {
  const key = tripKey(from, to);
  return favourites.value.some((f) => tripKey(f.from, f.to) === key);
}

export function removeRule(id) {
  rules.value = rules.value.filter((r) => String(r.id) !== String(id));
  writeRules();
}

/** One rule replaced by a new object, which is what the pure helpers return. */
function patchRule(id, fn) {
  const found = rules.value.find((r) => String(r.id) === String(id));
  if (!found) return null;
  const next = cleanRule(fn(found));
  rules.value = rules.value.map((r) => (String(r.id) === String(id) ? next : r));
  writeRules();
  return next;
}

// Muting is not switching off — see the note in `src/rules.js`. All four wrap
// the pure version so that the app and the server agree about what a mute is,
// down to which midnight "today" ends at.
export const muteRuleForAnHour = (id) => patchRule(id, (rule) => muteForAnHour(rule));
export const muteRuleForToday = (id) => patchRule(id, (rule) => muteForToday(rule, Date.now(), tzOffsetFor()));
export const unmuteRule = (id) => patchRule(id, (rule) => unmute(rule));

/** Off is "never again, until I say"; it leaves any mute alone underneath. */
export const setRuleActive = (id, on) => patchRule(id, (rule) => ({ ...rule, active: Boolean(on) }));
export const toggleRule = (id) => patchRule(id, (rule) => ({ ...rule, active: rule.active === false }));

// ── The mute set from a notification ─────────────────────────────────────────
//
// Two buttons under a disruption alert — "Mute this window" and "Mute today" —
// and they are the only controls in this app somebody uses without unlocking
// the phone. `prefs['lateagain:push-mute:v1']`:
//
//   { until: '2026-09-18T09:30:00.000Z', by: 'window' }
//
// WHICH IS NOT THE SOURCE OF TRUTH FOR A DEVICE THE SERVER PUSHES TO. The
// subscription document on Cloud Run is, because the sweep is what decides
// whether a phone buzzes, and a mute the phone believed in alone would be a
// button that visibly did nothing. This copy exists for the other two jobs:
// holding the app's OWN local alerts (the web, and a phone that could not
// register), and putting the mute on the Settings screen so it can be lifted
// by somebody who cannot remember setting it.
//
// localStorage rather than a store record, for the reason the quiet windows
// are: it is read SYNCHRONOUSLY, during the launch window, before IndexedDB
// has answered anything — and `announce()` asks on the very first poll.

const PUSH_MUTE = 'lateagain:push-mute:v1';

/** `{ until, by }`, or null for "nothing is muted". Never a stale object. */
export const pushMute = ref(readMute());

function readMute() {
  return cleanMute(prefs[PUSH_MUTE]);
}

/** A stored mute, or null. An expired one is not a mute — it is over. */
function cleanMute(row, now = Date.now()) {
  const state = row && typeof row === 'object'
    ? { until: typeof row.until === 'string' ? row.until : null, by: row.by === 'day' ? 'day' : 'window' }
    : null;
  return muteHolds(state, now) ? state : null;
}

function writeMute() {
  // A mute conjured by a stray /demo visit must never silence real alerts, and
  // under /demo it cannot: `prefs` is memory there. See src/core/storage.js.
  prefs[PUSH_MUTE] = pushMute.value;
}

/** Is the app's own local alerting muted right now? */
export function muteHoldsAlerts(now = Date.now()) {
  return muteHolds(pushMute.value, now);
}

/**
 * Drop a mute whose time has come.
 *
 * Called from the poll rather than from a timer: a mute ends by being ignored,
 * so the only thing this buys is the Settings row disappearing on its own,
 * and a ninety-second poll is a fast enough clock for a row that says 09:30.
 */
export function pruneMute(now = Date.now()) {
  if (!pushMute.value || muteHolds(pushMute.value, now)) return;
  pushMute.value = null;
  writeMute();
}

/** Record a mute this phone now believes in. `null` clears it. */
function setMute(state) {
  pushMute.value = cleanMute(state);
  writeMute();
  return pushMute.value;
}

// The contract, fixed, and shared with the iOS and Android handlers, the APNs
// payload and the FCM data block. Renaming any of the three on one side
// silently removes the buttons on that side and nowhere else.
export const DISRUPTION_CATEGORY = 'disruption';
export const MUTE_WINDOW_ACTION = 'mute-window';
export const MUTE_DAY_ACTION = 'mute-day';
// The third button, and the only one that opens the app. The other two are
// answered on the lock screen and leave it closed; this one exists because the
// alert now says what to do instead, and the next question is always "from
// where, and when".
export const SEE_OPTIONS_ACTION = 'see-options';

/**
 * Was that press "See options"?
 *
 * TWO SHAPES, because the two platforms disagree about where a button id
 * lives. Capacitor reports iOS's `UNNotificationResponse.actionIdentifier` as
 * `action.actionId`; our Android notification is built by hand in
 * `DisruptionMessagingService`, so its id travels as an extra on the intent and
 * arrives inside the notification's own data block. Reading only one of them is
 * a button that works on exactly one platform, with nothing to say which.
 */
export function isSeeOptions(action) {
  if (action?.actionId === SEE_OPTIONS_ACTION) return true;
  return action?.notification?.data?.actionId === SEE_OPTIONS_ACTION;
}

/** 'mute-window' and 'mute-day' as the server's `until` values, or null. */
export function muteActionFor(actionId) {
  if (actionId === MUTE_WINDOW_ACTION) return 'window';
  if (actionId === MUTE_DAY_ACTION) return 'day';
  return null;
}

/**
 * The headline this rule would want, or null — and never a thrown error.
 *
 * Wrapped because it is on the path of EVERY subscribe. The engine walks the
 * bundled network graph, and a bundle a city switch has left half-loaded, or a
 * rule whose stored path names a station that no longer exists, must cost this
 * phone a missing sentence and not its whole subscription: without the guard,
 * one bad rule stops the server being told about the watched lines, the saved
 * stations and the quiet hours as well.
 */
function fallbackForRule(rule) {
  try {
    const headline = fallbackHeadline({ rule, places: places.value, travel: travel.value });
    return headline ? { text: headline.text, routeIds: headline.avoids } : null;
  } catch {
    return null;
  }
}

/**
 * Mute this phone locally, computing the end the way the server would.
 *
 * `muteEndFor` is the pure function `server/push.mjs` calls, given the same
 * rules and the same offset — which is the whole reason it lives in the
 * import-free `src/rules.js`. A local alert's buttons and a pushed alert's
 * buttons must mean the same thing to the minute, or somebody muting "this
 * window" gets a different answer depending on whether they had signal.
 */
export function muteLocally(until, now = Date.now()) {
  const value = until === 'day' ? 'day' : 'window';
  return setMute({
    until: muteEndFor({ until: value, rules: rules.value, now, tz: tzOffsetFor(now) }),
    by: value,
  });
}

/**
 * A quick action on a SERVER push: tell the server, and believe what it says.
 *
 * IDEMPOTENT ON PURPOSE. The native handler answers the same button — it has
 * to, because the button works with the app not running — so this call is
 * usually the second one for the same press. Both compute the end from the
 * same stored rules, so the second write writes the same instant.
 *
 * A call that does not land still mutes locally. The person pressed a button;
 * the honest failure is being quieter than the server thinks, not a button
 * that did nothing.
 */
export async function muteFromPush(actionId, now = Date.now()) {
  const until = muteActionFor(actionId);
  if (!until) return null;

  const row = device.value;
  if (!isServerToken(row?.token)) return muteLocally(until, now);

  const res = await postMute(API_BASE, {
    token: row.token,
    platform: row.platform,
    until,
  });
  const state = muteStateFrom(res);
  if (!state) {
    console.warn(`push: mute failed (${res?.error ?? 'no answer'})`);
    return muteLocally(until, now);
  }
  return setMute({ until: state.until, by: state.by ?? until });
}

/**
 * Lift the mute, now. The server first, for the same reason `disablePush`
 * tells it before changing anything locally: a failure there must leave a
 * phone that is quieter than it wants to be, never one that believes it is
 * unmuted while the sweep is still holding its alerts.
 */
export async function liftMute() {
  const row = device.value;
  if (isServerToken(row?.token)) {
    const res = await postUnmute(API_BASE, { token: row.token, platform: row.platform });
    if (!res.ok) console.warn(`push: unmute failed (${res.error})`);
  }
  setMute(null);
  return true;
}

/**
 * `Muted until 09:30`, or `Muted until tomorrow 06:00`.
 *
 * The day is said only when it is not today, because "until 06:00" read at
 * 22:00 is ambiguous in exactly the direction that matters — somebody would
 * take it for six minutes ago and lift a mute they still wanted.
 */
export function describeMute(state, now = Date.now()) {
  if (!muteHolds(state, now)) return '';
  const end = new Date(state.until);
  const days = Math.round(
    (Date.parse(dayKey(end)) - Date.parse(dayKey(new Date(now)))) / 86_400_000,
  );
  if (days <= 0) return `Muted until ${clock(end)}`;
  if (days === 1) return `Muted until tomorrow ${clock(end)}`;
  return `Muted until ${end.toLocaleDateString('en-AU', { weekday: 'short' })} ${clock(end)}`;
}

/** A local calendar day as a sortable string, for comparing two of them. */
function dayKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** The Settings row's text, or '' when nothing is muted. */
export const pushMuteLabel = computed(() => describeMute(pushMute.value));

// ── Fares ────────────────────────────────────────────────────────────────────
//
// Which price a planned journey quotes. A preference rather than a setting with
// consequences: it changes a number on a card and nothing else, it is read on
// one screen, and it belongs in `prefs` rather than on a subscription because
// it has nothing to do with alerts.
//
// Concession is a real half of the travelling public — students, seniors,
// health-care card holders — and quoting them the full fare is quoting them
// roughly double. It is off by default because full fare is the answer that is
// never too low, which is the same rule the peak/off-peak choice follows.

const CONCESSION_FARES = 'lateagain:concession-fares';

export const concessionFares = ref(prefs[CONCESSION_FARES] === true);

/** Written through immediately — the settings screen has no Save, by design. */
export function setConcessionFares(on) {
  concessionFares.value = Boolean(on);
  prefs[CONCESSION_FARES] = concessionFares.value;
}

// ── Trips ────────────────────────────────────────────────────────────────────
//
// How much of a hurry a person is prepared to be in.
//
// The planner already knows how long a change takes at each station — see
// `changeMinutes` in journey.js, and the table it reads in city.js — and that
// is a fact about the concrete. This is the other half of the question, which
// is a fact about the person, and no table can hold it: somebody with a pram,
// a bag or a bad knee needs longer at the SAME station than somebody who
// doesn't, and neither of them is wrong.
//
// So it is a margin ON TOP rather than a replacement. Nought is the default
// because the table is already honest and a default that padded every change
// would start hiding real connections; two and five are there for the people
// the default is too brisk for.
//
// The key and the permitted values are journey.js's, imported rather than
// respelled: this ref and the planner's own read are the same value seen
// through two windows, exactly as `city.js` and the `prefs` proxy are.

export const changeMargin = ref(readChangeMargin());

function readChangeMargin() {
  const n = Number(prefs[CHANGE_MARGIN_KEY]);
  return CHANGE_MARGINS.includes(n) ? n : 0;
}

/** Set the margin. Anything not on the list is nought, which is the default. */
export function setChangeMargin(minutes) {
  const n = Number(minutes);
  changeMargin.value = CHANGE_MARGINS.includes(n) ? n : 0;
  prefs[CHANGE_MARGIN_KEY] = changeMargin.value;
}

// ── Places ───────────────────────────────────────────────────────────────────
//
// Where this person comes back to, and where they go in the morning.
//
// It exists for exactly one question: when the line home is suspended, what
// else gets you there? Answering it needs a point on the network and a distance
// somebody will walk, and this is the whole of what is kept — a stop id and one
// of three radii, per city, on this phone. No address, no coordinate, nothing
// sent anywhere; the server never learns where anybody lives, which is why the
// push only ever carries a precomputed headline and never the place behind it.
//
// PER CITY, always suffixed — see `placesKey`. Home in Melbourne is not home in
// Sydney, and a Carnegie stop id read against the Sydney bundle is not an error
// that throws, it is a reroute quietly computed from a station that is not
// there. `switchCity` re-reads this ref the way it re-reads `rules`.
//
// Travel is NOT per city: it is one sentence about the person.

export const places = ref(readPlaces(prefs[placesKey(city.value)]));

export const travel = ref(readTravel(prefs[TRAVEL_KEY]));

/** The walks on offer, so a screen cycling the chip has one list to read. */
export { WALK_RADII, radiusMetres } from './places.js';

function writePlaces() {
  prefs[placesKey(city.value)] = places.value;
}

/**
 * Set home or work.
 *
 * The radius comes with the station because the two together are the place: a
 * station with no radius cannot be used for anything, so a caller that has only
 * just been handed a station falls back to the middle option rather than
 * leaving a half-place behind. Anything the pure reader refuses clears the slot,
 * which is the same answer storage would have given on the next boot.
 */
export function setPlace(kind, { stopId, walkMinutes } = {}) {
  if (kind !== 'home' && kind !== 'work') return places.value;
  const minutes = WALK_RADII.includes(Number(walkMinutes))
    ? Number(walkMinutes)
    : (places.value[kind]?.walkMinutes ?? WALK_RADII[1]);
  places.value = readPlaces({ ...places.value, [kind]: { stopId, walkMinutes: minutes } });
  writePlaces();
  return places.value;
}

/** Forget one. The other is untouched. */
export function clearPlace(kind) {
  if (kind !== 'home' && kind !== 'work') return places.value;
  places.value = readPlaces({ ...places.value, [kind]: null });
  writePlaces();
  return places.value;
}

// ── The chosen set ───────────────────────────────────────────────────────────
//
// The stations somebody starred, which is now the only way a station is saved
// at all. `src/starred.js` carries the whole of the reasoning; the short of it
// is that nothing in this app could ever save a station on purpose — the board
// switcher did it behind your back on every switch — so a star beside a saved
// row would have been a second save. The two are one concept now.
//
// THESE ARE THE SAME `station` ROWS AS BEFORE, untouched and unextended. The
// widget, the watch, and the four screens that read "you" out of the default
// row all go on working because nothing about the storage moved; what changed
// is that `pick` no longer writes one, and a star does.
//
// PER CITY, as the rows always were, through the `city` on each row and the
// `forCity` view above.

/** Whether this station is in the chosen set, in the city on screen. */
export function isStationStarred(stopId) {
  return Boolean(starredRow(stopId));
}

/** The chosen row for this stop id, or null. */
export function starredRow(stopId) {
  const id = Number(stopId);
  if (!Number.isFinite(id) || id <= 0) return null;
  return stations.value.find((s) => Number(s.stopId) === id) ?? null;
}

/**
 * Star it, or unstar it. One gesture, both directions.
 *
 * A first star is made the default as well as chosen, because a set of one is
 * unambiguous about which station the board opens on and asking a second
 * question at that moment would be the app being pedantic. Every star after
 * that only joins the set: switching the board is `pick`, and saying which
 * station it OPENS on is `setDefaultStation`, and neither of those is what
 * somebody pressing a star on a search result meant.
 *
 * Unstarring the default is allowed. Refusing it would make the first station
 * anybody ever starred permanent, so the board is handed on instead — see
 * `defaultAfterRemoving`.
 */
export function toggleStar(station) {
  const id = Number(station?.stopId);
  if (!Number.isFinite(id) || id <= 0) return null;

  const held = starredRow(id);
  if (held) {
    const next = defaultAfterRemoving(stations.value, held.id);
    removeStation(held.id);
    if (next && !next.isDefault) saveStation({ ...next, isDefault: true });
    // The default station changed hands, so a tram stop it was opening beside
    // is no longer the choice either.
    if (held.isDefault) clearModeOpens();
    return null;
  }

  const first = stations.value.length === 0;
  // A first star is the first default: it takes the opening from a tram stop.
  if (first) clearModeOpens();
  return addStation({
    name: station.name,
    stopId: id,
    suburb: station.suburb ?? '',
    lines: Array.isArray(station.lines) ? station.lines.join(',') : String(station.lines ?? ''),
    isDefault: first,
  });
}

/**
 * Which chosen station the board opens on.
 *
 * Its own gesture, because it is its own decision. It used to be a side effect
 * of switching the board, which is how somebody looking up one station on a
 * Tuesday ended up with their Wednesday morning pointed at it.
 */
export function setDefaultStation(stopId) {
  const row = starredRow(stopId);
  if (!row) return null;
  clearModeOpens();
  for (const s of stations.value) {
    const mine = String(s.id) === String(row.id);
    if (Boolean(s.isDefault) !== mine) saveStation({ ...s, isDefault: mine });
  }
  return row;
}

/** The chosen rows, the one the board opens on first. */
export function starredStations() {
  const row = defaultRow(stations.value);
  if (!row) return [];
  return [row, ...stations.value.filter((s) => s !== row)];
}

/**
 * The one-time collapse of a city's rows, run once per city and recorded.
 *
 * Every row on an existing phone got there by being visited rather than
 * chosen, and there is no way to tell the station somebody uses from the four
 * they looked up once. The default survives — it is the board they open on and
 * the station the widget shows, and taking that away is the one outcome that
 * is not allowed — and the rest fall back to RECENT, where the switcher has
 * been putting them all along.
 *
 * NOTHING TO COLLAPSE UNDER /demo. The demo does not accumulate a history, it
 * is handed a set: `seedDemo` writes three stations as the chosen list of a
 * person who chose them, with Carnegie as the board. Tidying that would be the
 * app throwing away the very thing the demo exists to show, so the marker is
 * written and the rows are left alone. It is written rather than skipped so a
 * later call cannot change its mind; `prefs` is memory there, see
 * src/core/storage.js.
 */
export function collapseChosen() {
  const key = chosenKey(city.value);
  if (prefs[key] === true) return;
  if (isDemo.value) { prefs[key] = true; return; }
  const { keep, drop } = collapseToChosen(stations.value);
  for (const row of drop) removeStation(row.id);
  // The survivor is what the board was already opening on; say so on the row,
  // so a set of one is never a set with no default in it.
  if (keep[0] && !keep[0].isDefault) saveStation({ ...keep[0], isDefault: true });
  prefs[key] = true;
}

// Once the rows are off disk, and never before: a collapse run against an
// empty, still-hydrating ledger would record itself as done and then let the
// real rows through untouched.
stationsReady.then(() => collapseChosen());

/**
 * The station record behind a place, out of the city's bundle.
 *
 * Null for an unset place AND for a stop id this bundle does not have — a place
 * saved in Melbourne, read while Sydney is showing, or a station dropped from a
 * later bundle. Every caller has to handle "not set" already, so a station that
 * cannot be resolved is folded into the same answer rather than becoming a
 * second failure with its own branch.
 */
export function placeStation(kind) {
  const place = places.value[kind];
  if (!place) return null;
  return stationById(place.stopId) ?? null;
}

/**
 * Whether an option may put you on the buses PTV runs when the trains stop.
 *
 * Default OK, because a replacement bus is the operator's own answer and
 * refusing it by default would hide the fastest way home from somebody who
 * never had an opinion about it. AVOID is for the people who do.
 */
export function setReplacementBuses(value) {
  travel.value = readTravel({ replacementBuses: value });
  prefs[TRAVEL_KEY] = travel.value;
  return travel.value;
}

// ── First run ────────────────────────────────────────────────────────────────

// Has this install been through the welcome?
//
// In `prefs` — localStorage behind a proxy — rather than on a row, and for two
// reasons. Every other setting in this app lives on the SUBSCRIPTION records,
// and a first-run flag stored there does not exist until the screen it is
// supposed to gate has already run. And `prefs` reads SYNCHRONOUSLY, which
// matters because the boot decision below is made before IndexedDB has
// answered anything.
const ONBOARDED = 'lateagain:onboarded';

/**
 * Which question the welcome was on when it last lost the screen.
 *
 * The welcome is ONE page holding a step number, so anything that reloads the
 * WebView takes the number with it. `main.js` already refuses to apply an
 * over-the-air bundle while the welcome is up, because a permission prompt
 * backgrounds the app and the resume is exactly when a staged bundle lands —
 * and that is the case that used to restart onboarding mid-answer. This covers
 * everything else: a crash, a pulled-down refresh in a browser, a tab restored
 * a day later. The step's name, written on every move.
 *
 * In `prefs` beside the flag, for the same reason the flag is: it has to read
 * back synchronously, before anything has asked IndexedDB a question.
 */
export const WELCOME_STEP_KEY = 'lateagain:welcome-step';

/**
 * The step to resume on, raw: a step id, or a number an older build wrote.
 * `resumeStep` in src/welcome.js reads either.
 */
export function savedWelcomeStep() {
  return prefs[WELCOME_STEP_KEY] ?? 'stations';
}

/** Saved by name, so a step added later cannot shift it. A position is read as one. */
export function rememberWelcomeStep(step) {
  prefs[WELCOME_STEP_KEY] = typeof step === 'string' ? step : stepAt(step);
}

/** True once the welcome has been finished, skipped through, or made moot. */
export const onboarded = ref(prefs[ONBOARDED] === true);

export function markOnboarded() {
  prefs[ONBOARDED] = true;
  // Forgotten together with the flag. A resume point kept past the finish is a
  // welcome that reopens on step 4 the next time somebody asks to see it again.
  prefs[WELCOME_STEP_KEY] = 'stations';
  onboarded.value = true;
}

/** Settings' "Show the welcome again", and the way to test it. */
export function resetOnboarded() {
  prefs[ONBOARDED] = false;
  prefs[WELCOME_STEP_KEY] = 'stations';
  onboarded.value = false;
}

/**
 * The gating rule, on its own and pure.
 *
 * Three conditions now, and the middle one was missing.
 *
 * An install that already watches lines has done by hand everything the
 * welcome asks for, so it is treated as onboarded whatever the flag says.
 * Without that clause the first launch after this shipped would have
 * interrupted every existing user with a tour of settings they had already
 * set.
 *
 * BUT A WELCOME PART-WAY THROUGH LOOKS EXACTLY LIKE ONE OF THOSE INSTALLS, and
 * that is the bug. Step 1 writes the watched lines the moment Continue is
 * pressed — it has to, because the rest of the welcome reads them — so from
 * step 2 onward `watchedCount` is not zero. Anything that reloaded the WebView
 * there did not restart the welcome: it ENDED it. The person landed on the
 * departures board never having been asked about home, work, location or
 * alerts, and the gate stamped them onboarded on the way past, so they were
 * never asked again. A restart would have been the kinder failure.
 *
 * `welcomeStarted` is the difference between the two, and it is the stored
 * resume point: a phone that has moved past step 1 without finishing is in the
 * middle of the welcome, not out the other side of it.
 */
export function shouldOnboard({ onboarded: done, watchedCount, welcomeStarted = false }) {
  if (done) return false;
  if (welcomeStarted) return true;
  return Number(watchedCount) === 0;
}

/**
 * The same question, asked at boot, where the answer depends on an IndexedDB
 * read that has not finished yet.
 *
 * Bounded like `enablePush`: a WebView that never settles the hydration
 * promise would otherwise hold the first navigation open for ever, and a blank
 * screen is worse than a missed welcome. A timeout answers NO — showing the
 * welcome to somebody who may already have six lines, because their disk was
 * slow, is the one wrong answer with a cost.
 *
 * Neither /demo nor a QA run is ever onboarded. Demo data exists to show the
 * app in use, and a fixture screenshot of the welcome is a screenshot of the
 * wrong screen filed under the right name.
 */
export async function onboardingNeeded() {
  if (isDemo.value) return false;
  // A SHARE LINK IS ITS OWN WELCOME. It names the city and the station, which
  // is what the first two cards ask for, and somebody who followed a link sent
  // to them is asking to see one particular board rather than to set the app
  // up. Five questions in front of it is how a shared link becomes a link
  // nobody follows twice. The flag is not set, so the welcome is still there
  // for them the next time they open the app cold. See src/share-link.js.
  if (sharePending.value) return false;
  if (typeof window !== 'undefined' && window.__APPFACTORY_QA__) return false;
  if (onboarded.value) return false;

  const hydrated = await Promise.race([
    subscriptionsReady.then(() => true),
    new Promise((resolve) => setTimeout(() => resolve(false), 1500)),
  ]);
  if (!hydrated) return false;

  const watchedCount = watchedRouteIds.value.size;
  // A welcome that was interrupted part-way, which is not an existing install
  // however much its stored lines look like one. See `shouldOnboard`.
  const welcomeStarted = welcomeBegun(savedWelcomeStep());

  // An existing install. Record it, so the question is never asked again even
  // if they later remove every line.
  if (watchedCount > 0 && !welcomeStarted) { markOnboarded(); return false; }

  return shouldOnboard({ onboarded: onboarded.value, watchedCount, welcomeStarted });
}

// ── The device ───────────────────────────────────────────────────────────────

export const device = computed(() => devices.value[0] ?? null);
export const pushEnabled = computed(() => !!device.value?.pushEnabled);

// Is this a build that subscribes at all? The web and /demo never do, so every
// wait on the boot subscribe below has to be already over for them.
const NATIVE_SUBSCRIBE = !isDemo.value && isNative;

// Did the last subscribe to our own server succeed? THREE answers, not two.
//
//   null   nothing has come back yet — the launch window
//   true   a subscribe returned 200
//   false  a subscribe demonstrably failed, or there is nothing to subscribe
//
// Not persisted. It is a claim about right now — "the server has a token it can
// reach this phone with" — and a stale `true` read off disk at launch would
// silence the local fallback on a device that has since been wiped from the
// subscriptions collection.
//
// It starts `null` rather than `false` because `false` is a CLAIM, and at
// launch it was a false one: see `serverOwnsAlerts`, which reads the unknown
// window as the server's.
const subscribed = ref(null);

// Resolves when the boot subscribe has finished — landed, failed, or found
// nothing to do. Never rejects, and is already resolved wherever no subscribe
// is attempted.
//
// It exists because two things race at launch and the wrong one was winning.
// The foreground watcher calls `refresh()` the instant the app is visible,
// while the boot subscribe has an IndexedDB read and a network POST in front
// of it. Refresh got there first, `announce()` read a `subscribed` that had
// not been established yet, and the app locally re-announced the three
// cancellations the server had already pushed overnight — stamped 07:22, in
// the local format, because that is genuinely what fired them.
let settleSubscribe = () => {};
export const subscribeSettled = NATIVE_SUBSCRIBE
  ? new Promise((resolve) => { settleSubscribe = resolve; })
  : Promise.resolve();

/**
 * True when the SERVER is the thing that will wake this phone.
 *
 * This is the switch that stops the double buzz. Exactly one of the two paths
 * may fire for a given disruption: the server's push, or the app's own local
 * notification. A device with a real registration token AND a subscribe that
 * came back 200 has handed the job over; anything less keeps it.
 */
export const serverAlerts = computed(() => serverOwnsAlerts({
  token: device.value?.token,
  subscribed: subscribed.value,
}));

// The server's own "not entitled" (HTTP 402) to the last subscribe. A no, not a
// failure to reach it: see `localMayAlert` in src/push.js, which is why this
// is tracked apart from `subscribed`.
const refusedByServer = ref(false);

// The token and proof (as JSON) the server last said 402 not-entitled to, or
// null. The answer will not change until one of them does, so a subscribe carrying the
// same one is not sent: every edit to the watched lines used to re-POST and
// re-log the same refusal (one phone, eight refusals in a day). A purchase, a
// restore or a renewal changes the proof, and that subscribe goes out.
let refusedProof = null;

/**
 * Alerts are switched on here but this phone has not paid: a build that can
 * sell, and no entitlement. The Settings switch reads OFF in that state,
 * because that is what the phone will actually do.
 *
 * Always false on a build with no Billing plugin and on the web, which is how
 * the gated JS ships over the air to build 37 without walling anybody.
 */
export const alertsLocked = computed(() => billingAvailable.value && !entitled.value);

/**
 * Alerts are switched on here and the store has not answered yet.
 *
 * THE SWITCH USED TO FLASH ON. `billingAvailable` is false until the plugin
 * answers, so for the first seconds of every launch a phone that had not paid
 * read "Alerts ON", then flipped to OFF. On a build with the Billing plugin
 * that is a false ON; the switch says neither until it knows (bounded by
 * `READY_WAIT_MS`, and at once where there is no plugin or a stored yes).
 */
export const alertsPending = computed(() => pushEnabled.value && !billingKnown.value);

/** What the Alerts switch shows: on, and allowed to be. */
export const alertsOn = computed(
  () => pushEnabled.value && !alertsPending.value && !alertsLocked.value,
);

/**
 * The line the wall's demo is drawn in: the first one watched, or the demo's
 * own default. A function handed to billing, which cannot import this file.
 */
export const demoLine = computed(() => {
  const first = subscriptions.value[0];
  if (!first) return null;
  const line = lineFor(first.line);
  return line?.name && line.name !== 'Unknown line' ? { name: line.name, colour: line.colour } : null;
});
billing.setLineSource(() => demoLine.value);
// The vehicle the wall's hero rolls: the first watched line's own train in this
// city (a V/Line service, a Sydney Metro), else the city's default train.
billing.setRideSource(() => {
  const first = subscriptions.value[0];
  const line = first ? lineFor(first.line) : null;
  return { city: city.value, livery: liveryFor({ city: city.value, line }) };
});

/**
 * Ask for the notification permission, register with APNs/FCM, and tell our
 * server which lines this device cares about.
 *
 * Three steps, and the middle one is new. Until now the stored `token` was a
 * made-up `local-…` id, because there was nothing on the other end; it is now a
 * real registration token on a phone, and the `local-` form survives only for
 * the web and for a native device that could not register. Which one it is
 * decides who owns alerts — see `serverAlerts`.
 */
export async function enablePush() {
  // The OS prompt backgrounds the app; a staged bundle must not use that as
  // its cue to reload. See src/reload-guard.js.
  const release = holdReloads();
  try {
    return await enablePushNow();
  } finally {
    release();
  }
}

async function enablePushNow() {
  // Bounded. `devicesReady` is an IndexedDB read, and a WebView that never
  // settles it would leave this awaiting for ever — a toggle that does nothing,
  // reports nothing, and cannot be debugged from the outside. Two seconds is
  // far longer than the read needs; past that, proceed and let the write land
  // in memory.
  await Promise.race([devicesReady, new Promise((r) => setTimeout(r, 2000))]);
  const granted = await requestNotificationPermission();
  const row = device.value;
  const platform = detectPlatform();

  if (row) saveDevice({ ...row, pushEnabled: granted, platform });
  else {
    addDevice({
      token: `local-${platform}-${Math.random().toString(36).slice(2, 10)}`,
      platform,
      pushEnabled: granted,
      registeredAt: new Date().toISOString(),
    });
  }
  if (!granted) return false;

  // The web stops here, exactly as it always has: there is no registration to
  // make and no server push to receive, so the page notifies itself while it
  // is open and the Settings copy says so.
  if (!isNative) return true;

  const token = await registerForPush();
  if (token) {
    saveDevice({ ...device.value, token });
    await subscribeNow();
  }
  return true;
}

/**
 * The real registration token, or null.
 *
 * `addons/push` owns the plugin and emits the token on the bus, so this listens
 * BEFORE asking it to register — the event fires from a native callback that
 * can land before `register()` resolves, and arming the listener afterwards is
 * a race that loses on a fast phone.
 *
 * Imported dynamically, and for two reasons. The addon reaches
 * `src/core/state.js`, which touches `navigator` at module scope and would take
 * the whole vitest suite down with it. And a person who never turns alerts on
 * should never load the push plugin at all.
 */
const TOKEN_TIMEOUT_MS = 20_000;

async function registerForPush() {
  try {
    const [addon, { bus }] = await Promise.all([
      import('../addons/push/index.js'),
      import('./core/state.js'),
    ]);
    if (!addon.pushAvailable()) return null;

    let off = null;
    const arrived = new Promise((resolve) => {
      off = bus.on('push:token', (value) => resolve(String(value ?? '') || null));
      // A registration that never comes back must not hold this open for ever:
      // the toggle would sit spinning with nothing to report. Twenty seconds is
      // far beyond what APNs takes and short enough to be a fact on screen.
      setTimeout(() => resolve(null), TOKEN_TIMEOUT_MS);
    });

    const outcome = await addon.enablePush();
    if (outcome !== 'granted') { off?.(); return null; }

    // A device that registered on a previous launch already has one stored.
    const stored = addon.deviceToken();
    const token = stored || await arrived;
    off?.();
    return token && isServerToken(token) ? token : null;
  } catch {
    // No plugin, no native bridge, or a registration error. The local path
    // stays in charge, which is the whole reason it was kept.
    return null;
  }
}

/**
 * The tram stops this phone uses, by watched route, for the subscribe body.
 *
 * Read from the stars of every shown mode with stops, against its stop table
 * (loaded here, not at start: a phone with no stars never loads it). Failing
 * to load costs only the narrowing, and sends none, so every notice on a
 * watched route still reaches the phone.
 */
async function modeStopsToSend() {
  try {
    const [{ loadModeStops }, { tramStopsFor }] = await Promise.all([import('./mode-stops.js'), import('./tram-reach.js')]);
    const out = [];
    for (const mode of shownModes.value) {
      const stars = modeStars(mode);
      if (!stars.length) continue;
      const t = await loadModeStops(mode);
      if (!t) continue;
      const routes = [...watchedRouteIds.value].filter((id) => modeOfLine(id)?.id === mode.id);
      out.push(...tramStopsFor(t, stars, routes));
    }
    return out;
  } catch {
    return [];
  }
}

/**
 * Tell the server what this device watches. Returns whether it landed.
 *
 * Safe to call at any time: it is a no-op without a real token, and the server
 * upserts on the token, so calling it twice is calling it once.
 */
export async function subscribeNow() {
  // The demo's device row is a fiction — see `seedRows`. Its token would be
  // refused below anyway, but a demo must not so much as knock on the server:
  // a subscription document created from a store screenshot is a phone that
  // does not exist, in a sweep that will try to push to it.
  if (isDemo.value) {
    subscribed.value = false;
    return false;
  }
  const row = device.value;
  if (!row?.pushEnabled || !isServerToken(row.token)) {
    subscribed.value = false;
    return false;
  }
  // The purchase proof goes with it, so the first subscribe of a launch has to
  // wait for the store's answer — bounded, because a store that never answers
  // must not stop a phone being subscribed at all.
  await billing.whenReady();
  const modeStops = await modeStopsToSend();
  const body = buildSubscribeBody({
    token: row.token,
    platform: row.platform,
    subscriptions: subscriptions.value,
    stations: stations.value,
    // The windows and the rules the sweep will actually run, plus the same
    // thing said in the old vocabulary for a server that predates them.
    quietWindows: quietWindows.value,
    // Each rule with the headline for it worked out RIGHT NOW, and thrown away
    // again the moment the body is built. It is a function of three things that
    // all move — the rule's path, where home is, whether replacement buses are
    // acceptable — so a copy stored on the rule would be advice about a
    // commute somebody has since changed. See `rulesFor` in `src/push.js` for
    // what actually crosses the wire: a sentence and some line numbers.
    rules: rules.value.map((rule) => ({ ...rule, fallback: fallbackForRule(rule) })),
    settings: notificationSettings.value,
    // getTimezoneOffset is minutes to ADD to local to reach UTC, so Melbourne
    // is -600. Negated here so the field means minutes EAST of UTC.
    tzOffsetMinutes: -new Date().getTimezoneOffset(),
    // For the sweep split that does not exist yet. See buildSubscribeBody.
    city: city.value,
    notices: noticePushes.value,
    purchase: currentProof(),
    modeStops,
  });
  const proofKey = JSON.stringify([body.token, body.purchase ?? null]);
  if (refusedByServer.value && refusedProof === proofKey) {
    subscribed.value = false;
    return false;
  }
  const res = await postSubscribe(API_BASE, body);
  subscribed.value = res.ok;
  // 402 is the server saying this phone has not paid. The alerts stay the
  // server's to not send, the local path stays shut, and the wall is offered
  // where this build can sell.
  refusedByServer.value = !res.ok && isNotEntitled(res);
  refusedProof = refusedByServer.value ? proofKey : null;
  if (refusedByServer.value) entitlementRefused('refused');
  else if (!res.ok) console.warn(`push: subscribe failed (${res.error})`);
  // The subscription document is where a mute really lives — it is set from a
  // notification, which can happen with this app closed — so every subscribe
  // is also how this phone finds out it is muted, and how it finds out the
  // mute has expired. `muteStateFrom` answers null when the response says
  // nothing about it, and then nothing here is touched.
  const mute = muteStateFrom(res);
  if (mute) setMute(mute);
  return res.ok;
}

// Ticking six lines on the Choose lines screen is six store writes in about
// four seconds; six subscribe POSTs would all be correct and five wasted.
const resubscribe = debounced(() => { subscribeNow(); }, 2000);

export async function disablePush() {
  const row = device.value;
  if (row && isServerToken(row.token)) {
    // Told to stop BEFORE the local state changes, so a failure here leaves a
    // subscription the server will drop on its next dead-token sweep rather
    // than a phone that keeps being pushed to with nothing listening.
    await postUnsubscribe(API_BASE, row.token);
  }
  subscribed.value = false;
  if (isNative) {
    try {
      const addon = await import('../addons/push/index.js');
      await addon.disablePush();
    } catch { /* nothing registered; the permission is the OS's to revoke */ }
  }
  if (row) saveDevice({ ...row, pushEnabled: false });
}

function detectPlatform() {
  const p = typeof window !== 'undefined' ? window.Capacitor?.getPlatform?.() : null;
  return p === 'ios' || p === 'android' ? p : 'android';
}

/**
 * The LocalNotifications plugin, or null in a browser.
 *
 * IMPORTED, not read off `window.Capacitor.Plugins`. The global is populated by
 * the native bridge on its own schedule, so reading it early gives null and the
 * feature silently degrades to nothing on the platform it was written for.
 */
/**
 * The LocalNotifications plugin as a PLAIN OBJECT, or null off a phone.
 *
 * Why plain, and why not async: a Capacitor plugin is a Proxy that answers
 * EVERY property with a method call — including `then`. Return the proxy from
 * an async function, or `await` it, and the promise machinery asks it for
 * `.then`, gets a native call named "then", which iOS rejects as not
 * implemented, and the outer promise never settles. That was the whole
 * outage: 'Turn alerts on', the test alert and the diagnostics row all sat
 * for ever on a phone with no prompt and no error anywhere. So this is a
 * synchronous function returning an object with no `then`, and the plugin
 * proxy never crosses a promise boundary.
 */
function localNotifications() {
  const cap = typeof window !== 'undefined' ? window.Capacitor : null;
  if (!cap?.isNativePlatform?.()) return null;
  const p = cap.Plugins?.LocalNotifications ?? LocalNotifications;
  return {
    checkPermissions: (...a) => p.checkPermissions(...a),
    requestPermissions: (...a) => p.requestPermissions(...a),
    schedule: (...a) => p.schedule(...a),
    cancel: (...a) => p.cancel(...a),
    getPending: (...a) => p.getPending(...a),
    addListener: (...a) => p.addListener(...a),
    removeAllListeners: (...a) => p.removeAllListeners(...a),
    registerActionTypes: (...a) => p.registerActionTypes(...a),
  };
}

/**
 * Put the two quick actions under the app's OWN notifications, once.
 *
 * The server's pushes carry `category: 'disruption'` and the native side draws
 * their buttons; a LOCAL notification is drawn by this plugin, so the same two
 * buttons have to be declared here and named on every alert that is scheduled.
 * Same ids, same order, same words — a person who muted from a pushed alert
 * yesterday and a local one today must not have to learn the screen twice.
 *
 * Registration and the listener are done together and once, because a listener
 * attached twice fires twice and would double-post the mute. Failing is not
 * fatal: the alert still shows, just with nothing under it.
 */
let localActions = null;

function ensureLocalActions(api) {
  if (localActions) return localActions;
  localActions = (async () => {
    try {
      await api.registerActionTypes({
        types: [{
          id: DISRUPTION_CATEGORY,
          actions: [
            { id: MUTE_WINDOW_ACTION, title: 'Mute this window' },
            { id: MUTE_DAY_ACTION, title: 'Mute today' },
          ],
        }],
      });
    } catch {
      // An older plugin, or a platform that will not take them. The alert is
      // worth more than its buttons, so this is not allowed to stop it.
    }
    try {
      await api.addListener('localNotificationActionPerformed', (event) => {
        const until = muteActionFor(event?.actionId);
        // A LOCAL alert is muted LOCALLY, always: this path exists precisely
        // for the device the server is not pushing to, and asking a server
        // that does not hold this phone would 404.
        if (until) muteLocally(until);
      });
    } catch {
      // As above: no listener means the buttons do nothing, not that the
      // notification fails.
    }
  })();
  return localActions;
}

/**
 * Ask for permission to notify, honestly.
 *
 * This used to `return true` whenever `Notification` was undefined, with the
 * comment "native shell, or a browser without it". Both halves of that were
 * wrong, and together they were the whole bug:
 *
 *   * `Notification` is undefined in WKWebView and in Android's WebView,
 *     because the Web Notifications API does not exist there. So on every
 *     phone this returned true.
 *   * Returning true recorded `pushEnabled` and the Settings screen said
 *     "Alerts are on. You will hear about a disruption once."
 *
 * Nothing was ever asked and nothing could ever fire. The app did not fail —
 * it claimed to work. That is the worse failure, because nobody goes looking.
 */
// A bound on the permission prompt, generous on purpose: a person reads the
// iOS dialog, and on the simulator the grant came back 23 seconds after the
// request. Ten seconds turned a real "Allow" into "the phone said no". The
// hang this guarded against (a plugin proxy crossing a promise boundary) is
// fixed at its root in localNotifications(); this is now only a backstop.
const PERMISSION_TIMEOUT_MS = 90_000;
const withTimeout = (promise, ms, fallback) => Promise.race([
  promise,
  new Promise((resolve) => setTimeout(() => resolve(fallback), ms)),
]);

async function requestNotificationPermission() {
  const api = localNotifications();
  if (api) {
    try {
      const status = await withTimeout(api.requestPermissions(), PERMISSION_TIMEOUT_MS, null);
      if (status === null) throw new Error('permission prompt timed out');
      return status?.display === 'granted';
    } catch (e) {
      if (/timed out/.test(String(e?.message))) throw e;
      return false;
    }
  }
  // Web. A browser genuinely without the API cannot notify, so say so rather
  // than reporting success.
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  try {
    return (await Notification.requestPermission()) === 'granted';
  } catch {
    return false;
  }
}

// ── The poll, and the notify-once rule ───────────────────────────────────────

// The clock on the wall of this app, in one place.
//
// 24-hour, zero-padded, no seconds — the way a station indicator board writes a
// time and the way Martian Mono is set everywhere else here. Every "as of"
// stamp on every screen goes through this, so there is one format to be wrong.
//
// `tz` is for a station that keeps another zone's time (see `src/zone-time.js`):
// pass it and the time is that wall's. Left out, the device's, as ever.
export function clock(value, tz = null) {
  return wallClock(value, tz);
}

// When the feed was last READ, persisted.
//
// It survives a relaunch because the disruptions do. Reopening the app on a
// train with no signal shows the ledger it had, and a ledger with no age on it
// is a ledger claiming to be current — which is the one thing a status board
// must never do. Stored as an ISO string; `prefs` is synchronous, so the stamp
// is available before anything has rendered.
const LAST_REFRESH = 'lateagain:lastRefreshedAt';

function storedRefreshedAt() {
  const raw = prefs[LAST_REFRESH];
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

export const lastRefreshedAt = ref(storedRefreshedAt());
export const refreshing = ref(false);

/**
 * Has a poll of the feed finished since this launch — answered or failed?
 *
 * NOT `lastRefreshedAt`, which survives a restart: that is the time the rows
 * on disk were true as of, and on a phone shut since Tuesday it says Tuesday.
 * This is the narrower question — has THIS session heard back from PTV — and
 * the one thing that needs it is a shared link to a disruption, which arrives
 * while the ledger is still empty and must not conclude from an empty ledger
 * that the disruption is over. See `feedSettled`.
 *
 * A FAILED POLL COUNTS. Not because a failure is an answer, but because
 * nothing further is going to be learned by standing still: the rows on the
 * phone are all there will be for now, and holding a screen for another five
 * seconds to say the same thing is a worse answer, not a truer one.
 */
const feedAnswered = ref(false);

/**
 * How long a link will wait for the first feed before giving up on it.
 *
 * A poll is a second or two on a working connection. Six is long enough to
 * cover a slow one and short enough that a dead one does not hold a screen
 * hostage — and it is only ever spent when the answer is not already on the
 * phone, so an app that has been running for a minute waits zero.
 */
export const FEED_WAIT_MS = 6000;

/**
 * Resolve once this session has read a feed, or the wait runs out.
 *
 * `true` means a poll has been round and the rows below are as good as they
 * are going to get; `false` means the wait ran out with nothing back at all.
 * Either way the caller decides on the ledger it has — the difference is only
 * that after `true` an empty ledger means something.
 *
 * `refresh()` is KICKED OFF HERE rather than assumed: a cold start applies a
 * link before anything has polled, and waiting on a poll nobody started would
 * spend the whole six seconds and then say the wrong thing.
 */
export function feedSettled({ timeout = FEED_WAIT_MS } = {}) {
  if (feedAnswered.value) return Promise.resolve(true);
  // A no-op when one is already in flight, which is the ordinary case at boot.
  refresh().catch(() => { /* refresh never throws; this is belt and braces */ });
  return new Promise((resolve) => {
    let done = false;
    const finish = (ok) => {
      if (done) return;
      done = true;
      stop();
      clearTimeout(timer);
      resolve(ok);
    };
    const stop = watch(feedAnswered, (answered) => { if (answered) finish(true); });
    const timer = setTimeout(() => finish(false), timeout);
  });
}

/** Tests only: put the session back where a fresh launch starts. */
export function clearFeedAnswered() {
  feedAnswered.value = false;
}

/** Tests only: a poll has been round, without running one. */
export function markFeedAnswered() {
  feedAnswered.value = true;
}

/**
 * Why the last poll failed, or null.
 *
 * Read by the screens so a failure is visible rather than silent. `refresh()`
 * swallows the throw deliberately: the poll runs on a timer and on every
 * foreground, and an unhandled rejection every ninety seconds on a train in a
 * tunnel is noise, not information.
 */
export const refreshError = ref(null);

/**
 * Whether that failure was this app's own budget rather than the feed.
 *
 * The disruption poll asks at the highest priority there is and is the last
 * thing the gate ever refuses, but it can be refused while a board and a plan
 * are in flight — and "couldn't reach PTV" about a feed that is ninety seconds
 * old and perfectly alive is the most alarming sentence this app has.
 */
export const refreshBusy = ref(false);

/**
 * One poll of the cached disruption feed.
 *
 * TWO timestamps, and they are not the same thing. `seenAt` is the notify-once
 * rule: a row already carrying one has been considered, and a later poll
 * updates its text and leaves the stamp alone — that is what stops a signal
 * fault buzzing a phone every ninety seconds for an hour. `sentAt` is the
 * receipt: it exists only on rows a notification was actually fired for, and it
 * is what the Alerts screen lists. Stamping `seenAt` on insert and calling that
 * "sent" is why a fresh install with alerts OFF showed fifteen alerts it had
 * never sent.
 */
/**
 * What a live feed says about the rows we already hold.
 *
 * A disruption that PTV has stopped publishing is over, and a row that stays
 * on the ledger after that is a lie with a timestamp. This is also how the
 * sample feed leaves: its rows were saved like any other while the server was
 * unconfigured, and the first real feed reports them absent. Rows that were
 * announced are kept, marked, for the Alerts tab's history; the rest go.
 */
export function reconcile(rows, feedIds) {
  // Either bare PTV ids, or the feed's own rows. A row names ONE line, and PTV
  // files a multi-line notice once per line, so when the feed's rows are
  // passed a stored row is only still current if ITS line is still named —
  // a notice PTV narrows from three lines to one clears the other two.
  const items = [...feedIds];
  const byLine = items.some((f) => f && typeof f === 'object');
  const seen = new Set(items.map((f) => (byLine ? lineKey(f.ptvId, f.routeId) : String(Number(f)))));
  const keyOf = (d) => (byLine ? lineKey(d.ptvId, d.line) : String(Number(d.ptvId)));
  const stale = rows.filter((d) => !seen.has(keyOf(d)) && !d.clearedAt);
  return {
    clear: stale.filter((d) => d.sentAt),
    remove: stale.filter((d) => !d.sentAt),
  };
}

/**
 * How long a CLEARED alert stays in the Alerts history: seven days.
 *
 * A cleared row is kept so the history can say "you were told, and it is
 * over". Kept for ever, it became a list of week-old cancellations that read
 * as current news. A week covers "what happened on my commute this week";
 * a notice PTV is still publishing is never cleared, so it is never pruned
 * however old it is.
 */
export const CLEARED_KEEP_MS = 7 * 24 * 60 * 60 * 1000;

/** The stored rows whose clearing is older than `CLEARED_KEEP_MS`. Pure. */
export function expiredCleared(rows, now = Date.now()) {
  return (rows ?? []).filter((d) => {
    if (!d?.clearedAt) return false;
    const at = new Date(d.clearedAt).getTime();
    return Number.isFinite(at) && now - at > CLEARED_KEEP_MS;
  });
}

/** One stored row per PTV notice PER LINE. See the ingest in `refresh`. */
function lineKey(ptvId, line) {
  return `${Number(ptvId)}:${Number(line)}`;
}

export async function refresh() {
  if (refreshing.value) return;
  refreshing.value = true;
  try {
    // A mute ends by being ignored, but the Settings row saying so has to go
    // on its own. The poll is the app's clock; nothing else needs one.
    pruneMute();
    await Promise.all([disruptionsReady, subscriptionsReady]);

    let feed;
    try {
      feed = await fetchDisruptions();
    } catch (e) {
      // Nothing is written, nothing is cleared, and `lastRefreshedAt` keeps the
      // time the ledger on screen is actually true as of. The rows stay exactly
      // as they were: the alternative — emptying the ledger because we could
      // not reach a server — reads as "every line is fine".
      refreshError.value = e.message || `${couldntReach()}.`;
      refreshBusy.value = isBusy(e);
      return;
    }
    refreshError.value = null;
    refreshBusy.value = false;

    // EACH SHOWN MODE, FOR A PHONE THAT HAS IT, AND ON ITS OWN. A mode's feed
    // that fails costs its rows their update and nothing else: the trains are
    // already in hand, and the reconcile below leaves every stored row of that
    // mode alone rather than reading a failed request as "every notice is
    // over". With a mode off this is not asked at all, and the reconcile clears
    // whatever rows were left from when it was on.
    const failedModes = new Set();
    for (const mode of shownModes.value) {
      try {
        feed = [...feed, ...(await fetchModeDisruptions(mode))];
      } catch {
        failedModes.add(mode);
      }
    }

    const now = new Date().toISOString();
    const fresh = [];

    for (const item of feed) {
      const record = toRecord(item);
      // KEYED BY NOTICE AND LINE. PTV sends a notice on three lines as three
      // rows with one id; matching on the id alone saved each over the last,
      // so "Alamein, Belgrave and Lilydale lines: buses replace trains" lived
      // on Lilydale only and the other two ledgers said running to timetable.
      const key = lineKey(item.ptvId, item.routeId);
      const existing = storedDisruptions.value.find((d) => lineKey(d.ptvId, d.line) === key);
      // The same notice already held against another line. Its new line is
      // not news — the notice was seen, and announced if it was going to be —
      // so the row inherits both stamps rather than buzzing again. This is
      // also what stops the first poll after this fix from re-announcing every
      // multi-line notice a phone already holds on one of its lines.
      const sibling = existing ? null
        : storedDisruptions.value.find((d) => Number(d.ptvId) === Number(item.ptvId));
      if (existing) {
        // Back in the feed means back on the ledger, whatever it was before.
        saveDisruption({ ...existing, ...record, seenAt: existing.seenAt, sentAt: existing.sentAt ?? null, clearedAt: null });
      } else if (sibling) {
        const row = addDisruption({ ...record, seenAt: sibling.seenAt ?? now });
        const saved = saveDisruption({ ...row, ...record, seenAt: sibling.seenAt ?? now, sentAt: sibling.sentAt ?? null });
        // UNLESS NOBODY WAS TOLD. A sibling with no `sentAt` was not announced
        // — very often because its line is not one this person watches. PTV
        // lists "Alamein, Belgrave, Lilydale" with Alamein first, so the
        // Alamein row is the fresh one and a Belgrave commuter's row would
        // arrive here and never be considered at all. It goes to `announce`
        // with the rest; the watched-line filter there drops the lines nobody
        // asked about and `onePerNotice` keeps what is left to one buzz.
        if (!sibling.sentAt) fresh.push(saved);
      } else {
        // Two writes, deliberately. `addDisruption` allocates the id and builds
        // its row from the fields the product plan declares, so `type` and the
        // rest would be dropped; saving the merged row straight back puts them
        // in the same record. Do NOT move this into store.js — that file is
        // regenerated.
        const row = addDisruption({ ...record, seenAt: now });
        fresh.push(saveDisruption({ ...row, ...record, seenAt: now, sentAt: null }));
      }
    }
    // Only a real feed can say a disruption is over. The sample feed stands
    // in for an unreachable server and must not clear what a live one said.
    if (!usingSampleData.value) {
      // THE STORED ROWS, NOT THE MERGED ONES. A simulated row is in no feed by
      // definition, so reconciling the merged list against PTV would retire
      // every simulation on the next poll — ninety seconds after it started.
      const held = failedModes.size
        ? storedDisruptions.value.filter((d) => !failedModes.has(modeOfLine(d.line)))
        : storedDisruptions.value;
      const { clear, remove } = reconcile(held, feed);
      for (const row of clear) saveDisruption({ ...row, clearedAt: now });
      for (const row of remove) removeDisruption(row.id);
    }
    // History is a week, not for ever. Independent of the feed, so it runs on
    // the sample feed too: a stale row is stale whoever is answering.
    for (const row of expiredCleared(storedDisruptions.value)) removeDisruption(row.id);
    lastRefreshedAt.value = new Date();
    // Demo data never touches real storage — that rule holds for the stamp too,
    // or a stray /demo visit rewrites when the real app last saw the trains.
    // Under /demo this key is in memory (src/core/storage.js), so the write is
    // the same write and it lands nowhere.
    prefs[LAST_REFRESH] = lastRefreshedAt.value.toISOString();
    await announce(fresh);
  } finally {
    refreshing.value = false;
    // A POLL HAS BEEN ROUND, whichever way it went. In the `finally` so that
    // the failure path counts too: see `feedAnswered`.
    feedAnswered.value = true;
  }
}

function toRecord(item) {
  return {
    ptvId: item.ptvId,
    title: item.title,
    description: item.description,
    line: item.routeId,
    status: item.status,
    startedAt: item.startedAt,
    url: item.url,
    // PTV's own classification, verbatim: "Minor Delays", "Cancellation",
    // "Station detour". `severityOf` reads it first and the prose second, so
    // this field is the difference between a ledger that is right and one that
    // says DELAYED because a car park is shut.
    type: item.type ?? '',
    endsAt: item.endsAt ?? null,
    stopScoped: Boolean(item.stopScoped),
    // The stations PTV names on the notice, as ids and names. Older rows in
    // storage predate the field, so every reader treats it as optional.
    stops: Array.isArray(item.stops) ? item.stops : [],
    updatedAt: item.updatedAt ?? null,
    publishedAt: item.publishedAt ?? null,
    // None of these are in the product plan either, which is why every write
    // goes through `saveDisruption` rather than `addDisruption` alone.
    delayMinutes: item.delayMinutes ?? null,
  };
}

/**
 * Notify for disruptions seen for the first time, on watched lines, outside
 * quiet hours, that are actually about the trains. Silent about everything
 * else — including a line the person unticked five minutes ago, and including
 * every station notice PTV files against a line.
 *
 * A row is stamped `sentAt` only when a notification was really handed to the
 * OS. Nothing in here stamps on the way past.
 */
// How long to wait for the boot subscribe before deciding who owns a buzz.
//
// Bounded for the same reason `enablePush` bounds `devicesReady`: a phone with
// no signal never settles that POST, and a fallback silenced for ever by a
// dead network is the exact failure the fallback exists to prevent. Past this,
// the local path proceeds — which is the right answer for a phone that cannot
// reach us, because a server it cannot reach cannot have pushed to it either.
const SUBSCRIBE_ANSWER_MS = 4000;

/**
 * A stored disruption in the shape `src/rules.js` reads.
 *
 * One field, and it is the one that matters: this app's rows name their line
 * `line`, because that is what the product plan called it, and `touches` asks
 * for `routeId`, because that is what PTV and the server call it. Handing the
 * row over unadapted loses tier 3 silently — every rule stops matching on its
 * line and only a notice that names one of the trip's stops gets through.
 */
function asNotice(row) {
  return {
    routeId: Number(row.line),
    stops: Array.isArray(row.stops) ? row.stops : [],
    // Whether PTV tied the notice to stops: the tram narrowing needs it.
    stopScoped: Boolean(row.stopScoped),
  };
}

/**
 * `affectedStops`, or a function that finds none.
 *
 * Dynamic for the same reason `resolveRulePath` is: `src/network.js` carries
 * the bundled network, and importing it here would put 54 KB of station data
 * in the chunk a cold open downloads. It is fetched the first time a
 * disruption is actually announced — by which point the app has been running
 * for at least one poll — and cached by the module loader after that.
 *
 * Failing to load it costs only tier 2 of `touches`: the rule still matches on
 * the stops PTV names and on the line. That is the right way round — the app
 * over-notifies rather than going quiet.
 */
async function affectedStopsReader() {
  try {
    const { affectedStops } = await import('./network.js');
    return affectedStops;
  } catch {
    return () => [];
  }
}

/**
 * For a notice on a tram route: the stops this phone uses on it, the route's
 * order, and the stops its prose names — `src/rules.js` decides what they mean.
 *
 * `() => ({})` for anything else, and when the stop table cannot be loaded:
 * the notice is then route-wide, as it was before there were stops.
 */
async function tramReachReader() {
  const tables = new Map();
  try {
    const [{ loadModeStops, affectedStops }, { minesOn, orderOf }] = await Promise.all([
      import('./mode-stops.js'), import('./tram-reach.js'),
    ]);
    for (const mode of shownModes.value) {
      const stars = modeStars(mode);
      if (!stars.length) continue;
      const t = await loadModeStops(mode);
      if (t) tables.set(mode.id, { t, stars, mode });
    }
    return (row) => {
      const held = tables.get(modeOfLine(row.line)?.id);
      if (!held) return {};
      const text = `${row.title ?? ''} ${row.description ?? ''}`;
      return {
        mine: minesOn(held.t, held.stars, row.line),
        order: orderOf(held.t, row.line),
        affected: affectedStops(held.t, row.line, text, { numberWord: held.mode.labels.stop }),
      };
    };
  } catch {
    return () => ({});
  }
}

// Exported for the suite, and for nothing else — no screen calls it. The four
// gates below (permission, who owns the buzz, the mute, the rules) are the
// whole of whether a phone goes off at 6:40am, and a gate that can only be
// reached through a live PTV feed is a gate that gets tested by being shipped.
export async function announce(rows) {
  if (!rows.length || !pushEnabled.value) return;

  // Who owns the buzz is not readable until the boot subscribe has answered.
  // Asking early is asking during the one window where the answer is unknown,
  // and the unknown answer used to read as "the app's" — which is how a phone
  // that had been pushed to all night announced the same three cancellations
  // again the moment it was opened.
  await Promise.race([
    subscribeSettled,
    new Promise((resolve) => setTimeout(resolve, SUBSCRIBE_ANSWER_MS)),
  ]);

  // The server owns this device's alerts, so the app must not also fire one.
  // Two notifications for one signal fault is worse than either alone: the
  // second arrives with nothing to distinguish it from a new problem, and the
  // person learns to ignore both. The local path stays for the web, and for a
  // phone that granted permission but could not register or could not reach
  // the server — where a missed alert is the failure this app exists to stop.
  //
  // AND NOT FOR A PHONE THAT HAS NOT PAID. Where this build can sell and the
  // phone is not entitled, or the server answered 402, nothing fires locally
  // either: a failed subscribe used to hand alerts to the app, and that was a
  // free way round the subscription.
  if (!localMayAlert({
    token: device.value?.token,
    subscribed: subscribed.value,
    refused: refusedByServer.value,
    gated: !mayAlert(),
  })) return;

  // A mute from a notification's quick actions HOLDS, exactly as a quiet
  // window does: the rows go unstamped, so a disruption still running when the
  // mute ends is announced then. Muting "this window" is not "forget this".
  if (muteHoldsAlerts()) return;

  // Quiet, and the rules, and the trip they describe — one decision, made by
  // the file the server runs. A `no` here leaves the row UNSTAMPED, which is
  // the hold-until-later behaviour the quiet gate always had: the same row is
  // reconsidered on the next poll, and a disruption that starts at 3am is
  // announced at six if it is still going.
  const watched = watchedRouteIds.value;
  const now = Date.now();
  const tz = tzOffsetFor(now);
  const affectedStops = await affectedStopsReader();
  const tramReach = await tramReachReader();

  const mine = rows.filter((row) => {
    if (!watched.has(Number(row.line)) || !ALERTABLE.has(severityOf(row))) return false;
    // A tram notice is measured against the stops this phone uses on the
    // route (`outOfReach`, in the same file the sweep runs). Anything else
    // takes the prose reading it always did.
    const reach = isAdapterLine(row.line) ? tramReach(row) : null;
    const affected = reach ? reach.affected ?? [] : affectedStops(row.line, `${row.title ?? ''} ${row.description ?? ''}`);
    const opts = reach ? { affected, mine: reach.mine, order: reach.order } : { affected };
    // THE DEMO ASKS A SLIGHTLY DIFFERENT QUESTION, and it is the honest one to
    // ask at three in the afternoon. The quiet windows and the rule's own
    // 08:00–09:30 window decide WHEN a phone buzzes, not WHOSE disruption it
    // is: a fault on the Pakenham line at 15:00 is still this person's, held
    // until the morning. Somebody looking at the demo is looking at the Alerts
    // tab now, so it lists what the seeded rule touches — the same `touches`
    // the server sweep runs, minus the clock. Nothing is fired either way.
    if (isDemo.value) {
      return rules.value.some((rule) => rule.active !== false
        && touches(rule, asNotice(row), opts));
    }
    return shouldNotify(
      { rules: rules.value, quiet: quietWindows.value },
      asNotice(row),
      now,
      tz,
      opts,
    ).notify;
  });
  if (!mine.length) return;

  // A demo never asks the OS for anything. `stampSent` is what puts a row on
  // the Alerts tab, and it is the whole of what a notification would have done
  // that is visible on screen afterwards — so the tab shows the live
  // disruptions that would have alerted, labelled exactly as the real app
  // labels them, and no phone buzzes because a stranger opened a demo.
  if (isDemo.value) {
    stampSent(mine);
    return;
  }

  const api = localNotifications();
  if (api) {
    try {
      // The buttons, declared before the first alert that names them. iOS
      // silently draws no actions for a category it has not been told about.
      await ensureLocalActions(api);
      await api.schedule({
        notifications: onePerNotice(mine).map((row) => ({
          // Derived from the PTV id, not a counter. LocalNotifications treats a
          // repeated id as the same notification, so this is the notify-once
          // rule expressed a second time where the OS can enforce it: two polls
          // that both see disruption 302511 cannot produce two buzzes.
          id: notificationId(row.ptvId),
          title: lineFor(row.line).name,
          body: row.title,
          // The two quick actions. Named on the alert, not on the plugin: a
          // registered category with nothing referencing it draws nothing.
          actionTypeId: DISRUPTION_CATEGORY,
          // No `schedule.at` — this is news, and news is delivered now.
        })),
      });
      stampSent(mine);
    } catch {
      // A notification that cannot be shown is not a reason to stop polling —
      // and not a reason to claim on the Alerts screen that it was sent.
    }
    return;
  }

  // Web.
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  for (const row of onePerNotice(mine)) {
    try {
      new Notification(lineFor(row.line).name, { body: row.title, tag: `la-${row.ptvId}` });
      stampSent(mine.filter((r) => Number(r.ptvId) === Number(row.ptvId)));
    } catch {
      // As above.
    }
  }
}

/** The first row of each notice: one buzz for a notice on three lines. */
function onePerNotice(rows) {
  const seen = new Set();
  return rows.filter((row) => {
    const key = Number(row.ptvId);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Record that these rows were announced. Persisted exactly as `seenAt` is. */
function stampSent(rows, at = new Date().toISOString()) {
  for (const row of rows) {
    // Simulated rows carry their own stamp and are never written back: a
    // stamp here would be this app recording, on disk, that it had told
    // somebody about a suspension that never happened.
    if (row?.simulated) continue;
    const live = storedDisruptions.value.find((d) => String(d.id) === String(row.id)) ?? row;
    saveDisruption({ ...live, sentAt: at });
  }
}

// ── Staying current while the app is open ────────────────────────────────────

// Matches the server's cache TTL: polling faster only re-reads the same ninety
// second old answer, and polling not at all is how a phone left on a desk at
// 8:05 still says "running to timetable" at 8:40. Nothing runs while the app is
// in the background — a hidden tab burning a request a minute is a battery
// complaint and an upstream courtesy failure at once.
const POLL_MS = 90_000;
let pollTimer = null;

function startPolling() {
  // The live demo polls like any other copy of the app — that is what makes it
  // live. The sample demo does not: its feed is a function of the clock and
  // re-reading it every ninety seconds would only redraw the same three rows.
  if (pollTimer || sampleData.value) return;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  pollTimer = setInterval(() => { refresh(); }, POLL_MS);
}

function stopPolling() {
  if (!pollTimer) return;
  clearInterval(pollTimer);
  pollTimer = null;
}

/** Stop polling, and let the open screens note what they were showing. */
function pause() {
  stopPolling();
  import('./foreground.js')
    .then(({ notifyBackground }) => notifyBackground())
    .catch(() => { /* boot graph; pages subscribe themselves */ });
}

/** Poll now and keep polling; used by every "we are visible again" signal. */
function resume() {
  refresh();
  startPolling();
  // The subscription too: a renewal, a refund or a lapse that happened while
  // the app was away, and the lapse notice if it is due.
  billing.resume().catch(() => { /* the store did not answer; keep what we had */ });
  // THE OPEN TRAIN / JOURNEY / LINE, which only listened to visibilitychange
  // and so often never heard that the phone had come back. iOS will not let
  // those pages poll in a pocket; this is the first moment they can run again.
  import('./foreground.js')
    .then(({ notifyForeground }) => notifyForeground())
    .catch(() => { /* boot graph; pages subscribe themselves */ });
  // AND THE LOCK SCREEN CARD, wherever in the app somebody happens to be.
  //
  // "Opened the app and it still says the reading is old" was a true report:
  // the only thing that refreshed a followed train was the train card's own
  // poll, so unless somebody landed on that exact screen the card had no route
  // back to being fresh. Coming to the front is the one moment the app can
  // always act on, so it acts on it. Imported lazily because the follow bridge
  // pulls the native plugin wrapper in with it and the web build has no use
  // for either.
  import('./follow-bridge.js')
    .then(async ({ restoreFollow, refreshFollow }) => {
      // WITHOUT RESTORE, a killed or freshly opened app has nothing in
      // `following`, so refresh is a no-op and a stale ARRIVED sits until
      // somebody swipes it. Ask the system what is actually on the lock
      // screen first; then refresh (or end) that card.
      await restoreFollow();
      return refreshFollow();
    })
    .catch(() => { /* no bridge on this platform; nothing to refresh */ });
}

/**
 * Wire the poll to the app being in front of someone.
 *
 * `visibilitychange` covers the web and both WebViews. `appStateChange` covers
 * the case a WebView does not always report — the phone being unlocked back
 * onto an app that was never hidden — and is imported lazily and guarded, the
 * way LocalNotifications is, so the web build does not pull a native plugin.
 */
function watchForeground() {
  if (typeof document === 'undefined' || sampleData.value) return;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') resume();
    else pause();
  });

  const cap = typeof window !== 'undefined' ? window.Capacitor : null;
  if (!cap?.isNativePlatform?.()) return;
  import('@capacitor/app')
    .then(({ App }) => App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) resume();
      else pause();
    }))
    .catch(() => { /* plugin absent; visibilitychange already covers this */ });
}

/**
 * What this device actually reports, as one line of plain text.
 *
 * Both notification bugs so far were invisible from the interface — the app
 * claimed alerts were on while being structurally unable to show one, and then
 * a control did nothing at all and said nothing. This puts the facts on the
 * screen so a bug report is a sentence rather than a guess.
 */
export async function notificationDiagnostics() {
  const cap = typeof window !== 'undefined' ? window.Capacitor : null;
  const parts = [`platform ${cap?.getPlatform?.() ?? 'web'}`];
  parts.push(`native ${cap?.isNativePlatform?.() ? 'yes' : 'no'}`);

  let api = null;
  try {
    api = localNotifications();
    parts.push(`plugin ${api ? 'loaded' : 'not native'}`);
  } catch (e) {
    parts.push(`plugin FAILED (${String(e.message).slice(0, 40)})`);
  }

  if (api) {
    try {
      const status = await api.checkPermissions();
      parts.push(`permission ${status?.display ?? 'unknown'}`);
    } catch (e) {
      parts.push(`permission check failed (${String(e.message).slice(0, 40)})`);
    }
  } else {
    parts.push(`web Notification ${typeof Notification === 'undefined' ? 'absent' : Notification.permission}`);
  }

  parts.push(`stored ${pushEnabled.value ? 'on' : 'off'}`);
  return parts.join(' · ');
}

/**
 * Fire one notification, now, to prove the plumbing works.
 *
 * This exists because of the bug it would have caught. The app reported
 * "Alerts are on" for weeks while being structurally incapable of showing a
 * notification on any phone, and nothing in the interface could have told you.
 * An app that promises to interrupt you is untrustworthy until it has done it
 * once, on your device, where you can see it.
 *
 * Returns what happened rather than throwing, so Settings can say which of the
 * three failures it was: not permitted, not supported, or sent.
 */
export async function sendTestAlert() {
  // One of the three gates (PAYWALL.md). A no-op where nothing is sold.
  if (!(await requireEntitlement('test'))) return { ok: false, reason: 'not-entitled' };
  const api = localNotifications();
  if (api) {
    try {
      let status = await api.checkPermissions();
      // Never asked yet is not a refusal: this button is the one place a
      // person is explicitly asking to see a notification, so ask the phone.
      if (status?.display === 'prompt' || status?.display === 'prompt-with-rationale') {
        status = await withTimeout(api.requestPermissions(), PERMISSION_TIMEOUT_MS, status);
      }
      if (status?.display !== 'granted') return { ok: false, reason: 'denied' };
      // With its buttons, because this is the one notification somebody asked
      // to see. A quick action that only appears on a real 6:40am disruption is
      // a quick action nobody has ever checked works.
      await ensureLocalActions(api);
      await api.schedule({
        notifications: [{
          id: 1,
          title: 'Late Again',
          body: 'This is what a disruption alert looks like.',
          actionTypeId: DISRUPTION_CATEGORY,
        }],
      });
      return { ok: true };
    } catch (e) {
      return { ok: false, reason: 'failed', detail: e.message };
    }
  }
  if (typeof Notification === 'undefined') return { ok: false, reason: 'unsupported' };
  if (Notification.permission !== 'granted') return { ok: false, reason: 'denied' };
  try {
    new Notification('Late Again', { body: 'This is what a disruption alert looks like.' });
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: 'failed', detail: e.message };
  }
}

/**
 * A stable 32-bit id for a PTV disruption.
 *
 * LocalNotifications requires a Java int on Android, so a raw PTV id — which
 * can exceed 2^31 — is rejected outright and the notification never appears.
 */
function notificationId(ptvId) {
  return Math.abs(Number(ptvId) || 0) % 2_000_000_000;
}

// ── Keeping the server's copy current ────────────────────────────────────────

// Two jobs, both native-only and both no-ops without a real registration token.
//
//   * Re-subscribe when the watched lines, saved stations, quiet windows or
//     commute rules change, so the server's idea of this device does not drift
//     from the person's. Debounced, because Choose lines writes once per tick —
//     and so does dragging a time field across an hour.
//   * Re-subscribe at launch, which is also what restores `subscribed` — it is
//     deliberately not persisted, so until this lands the app alerts itself.
if (NATIVE_SUBSCRIBE) {
  // `places` and `travel` are in here because the fallback headline is built
  // out of them. Moving home is not a change to any rule, and without these the
  // server would keep pasting yesterday's suburb into tomorrow's alert until
  // some unrelated edit happened to trigger a resubscribe.
  watch([subscriptions, stations, quietWindows, rules, places, travel, noticePushes, () => shownModes.value.map((m) => modeStars(m).join(','))], () => {
    if (!isServerToken(device.value?.token)) return;
    resubscribe();
  }, { deep: true });

  // A purchase, a restore or a renewal: tell the server at once, with the
  // proof, so alerts come back without another tap.
  watch(entitled, (now, was) => {
    if (!now || was === now || !isServerToken(device.value?.token) || !pushEnabled.value) return;
    subscribeNow();
  });

  Promise.all([devicesReady, subscriptionsReady])
    .then(() => subscribeNow())
    .catch(() => {})
    // Settled either way, and only here. `announce()` is waiting on this, and a
    // rejected IndexedDB read that never resolved it would hold the local path
    // at the timeout on every poll for the life of the session.
    .finally(() => settleSubscribe());

  // A push that arrives while the app is open is delivered silently by both
  // platforms, and the Alerts tab would sit showing the state from before it.
  // The notification is a signal that PTV has something new, so the app goes
  // and reads it rather than trusting the payload.
  //
  // The payload is trusted for exactly one thing: WHICH disruptions were just
  // pushed. Stamping those rows is what stops the next poll — ninety seconds
  // later, or the next foreground — announcing locally what the person has
  // already been told. The OS cannot do it: `notificationId(ptvId)` and an
  // APNs notification are unrelated id spaces, so a local notification for a
  // disruption already pushed is a second, indistinguishable buzz.
  import('./core/state.js')
    .then(({ bus }) => bus.on('push:received', async (notification) => {
      // After the refresh, not before: the rows to stamp may not exist yet.
      await refresh();
      stampPushed(notification);
    }))
    .catch(() => { /* no bus, no native shell; the poll still covers it */ });

  // The quick actions on a pushed alert, heard from JavaScript.
  //
  // ATTACHED HERE AND NOT THROUGH THE ADDON. `addons/push/index.js` already
  // listens for this event, but it re-emits only `action.notification` — the
  // `actionId`, which is the entire content of a button press, does not survive
  // the trip. The addon is chassis code the factory regenerates, so this is a
  // second listener on the same plugin rather than an edit to it. Both fire;
  // only this one can tell the two buttons apart.
  //
  // The native handler answers the same press, and must — the buttons work with
  // the app closed. This one is for the press that happens while it is open,
  // and it is safe when both run: see `muteFromPush`.
  try {
    const Push = plugin('PushNotifications');
    Push?.addListener?.('pushNotificationActionPerformed', (action) => {
      if (isSeeOptions(action)) { openOptions(); return; }
      if (!muteActionFor(action?.actionId)) return;
      muteFromPush(action.actionId).catch(() => { /* reported by muteFromPush */ });
    });
  } catch {
    // No plugin on this build. The native side still answers the buttons.
  }
}

/**
 * "See options", pressed on an alert: stash the request and say where to go.
 *
 * TWO STEPS AND A BUS, because this file cannot import the router. `app-state`
 * is imported by every page and by the vitest suite; importing `src/router.js`
 * here would pull the whole page graph into the first chunk and would make the
 * store untestable outside a browser. So the navigation is ANNOUNCED and the
 * app shell — which already lives inside the router — does the push.
 *
 * The request is stashed first. The Options screen reads it as it enters, and
 * an empty request is a real one: it means "work out the from-station yourself",
 * which is what a button on a lock screen can possibly know.
 */
function openOptions() {
  import('./options-request.js')
    .then(({ requestOptions }) => { requestOptions({}); })
    .catch(() => { /* the screen falls back to its own defaults */ })
    .then(() => import('./core/state.js'))
    .then(({ bus }) => bus.emit('navigate', '/tabs/options'))
    .catch(() => { /* no bus, no shell; nothing to navigate */ });
}

/** Mark the rows a push just named as already told, if we hold them. */
function stampPushed(notification) {
  const wanted = new Set(pushedDisruptionIds(notification));
  if (!wanted.size) return;
  const rows = storedDisruptions.value.filter((d) => wanted.has(Number(d.ptvId)) && !d.sentAt);
  if (rows.length) stampSent(rows);
}

// ── Demo mode, and the QA fixtures ───────────────────────────────────────────

// WHAT `/demo` IS NOW: the real app, on the real feed, with somebody's first
// evening of setting-up already done.
//
// It used to be the real app on invented data, and that was the wrong trade in
// both directions. A board of trains that were never timetabled is the one
// screenshot a departures app must not take — the person looking at it can see
// the real 8:14 from the platform they are standing on — and every screen that
// depends on the network answering (the trip planner, the live line map, the
// station facilities) was showing a frozen picture of itself. Meanwhile the
// thing a demo is actually for, SHOWING THE APP IN USE, needs no invented
// trains at all: it needs watched lines, saved stations, a commute rule and
// alerts already on, which is exactly what is seeded below.
//
// The generated store seeds `/demo` with rows built from field names — a
// subscription pointing at line -1, a disruption called "Disruption 1". Real
// enough to prove the wiring, useless as a store screenshot, and the line
// colours the whole design rests on come out blank. So demo mode is seeded
// here instead, with the network as it actually is.
//
// NOTHING HERE IS SAVED, and that is now enforced in one place rather than
// thirty: `src/core/storage.js` swaps both stores for memory in demo mode, so
// a live disruption landing on the ledger, a station saved from the board, a
// cached board and a planned trip all go to memory and are gone on reload.
// The per-writer `isDemo` guards that used to stand in for this are gone with
// it — they were narrower than the rule and, once the demo started switching
// cities, actively wrong: a `writeRules` that refused to write lost the
// seeded commute on the way back from Sydney, because the city switch re-reads
// the key it declined to set.
//
// The SAMPLE data is still here, reached through `/demo?fixture=sample` and by
// every QA run — see `src/demo.js` for why a screenshot harness needs a frozen
// answer. That path keeps the behaviour it has always had, including the QA
// FIXTURES, which are a separate job and the one that is easy to miss. The
// product plan declares three states for Home — no-lines-yet, all-good,
// two-lines-disrupted — and QA renders each of them and compares. Seeding one
// fixed set made all three come out identical, and the audit said so: "the
// fixture changed nothing. A state that cannot be produced is audited in name
// only."
//
//   plain /demo               live. Seeded lines, stations, rule, quiet hours
//                             and alerts; the trains and the disruptions come
//                             from PTV, and the first poll runs immediately.
//   /demo?fixture=sample      the frozen set, and no polling.
//   QA, seed `empty`          nothing. The empty state is the point.
//   QA, seed null (default)   every watched line running fine.
//   QA, seed `populated`      two lines disrupted.
//
// Negative ids throughout, exactly as the store expects: `persist()` refuses
// them, so even if the memory store were ever swapped back for the real one a
// curious tap on /demo could not write sample rows into somebody's app.

const DEMO_MINUTE = 60_000;

/** What QA asked for, if QA is asking. `null` outside a QA run. */
function qaFixture() {
  if (typeof window === 'undefined' || !window.__APPFACTORY_QA__) return null;
  try {
    return localStorage.getItem('appfactory:qa:seed') ?? 'default';
  } catch {
    return 'default';   // private mode; treat it as the plain fixture
  }
}

const sub = (id, routeId) => ({
  id, entity: 'subscription', line: routeId, active: true,
  quietFrom: '21:00', quietUntil: '06:00', weekdaysOnly: true,
});

function demoDisruptions(now) {
  return [
    // SUSPENDED, on both lines through the seeded home station.
    //
    // It was one row of Major Delays, which read well on the ledger and is not
    // a fault this app can do anything about: `isSerious` wants a suspension,
    // a cancellation, or twenty minutes of delay with a figure attached, and
    // "delays of up to 20 minutes" in prose carries no figure. So every screen
    // built to answer a serious disruption was invisible in the demo.
    //
    // Two rows rather than one, because Carnegie is on both lines and a demo
    // that suspended only one of them would offer the other from the same
    // platform — a true answer, and not the one this feature exists for. With
    // the pair down, the way home is the Glen Waverley line to East Malvern
    // and 1.4 km on foot, which is the case the whole design was drawn around.
    {
      id: -101, entity: 'disruption', ptvId: 302511, line: 4,
      title: 'Cranbourne line suspended', status: 'current', type: 'Suspended',
      description: 'Trains are not running on the Cranbourne line due to an '
        + 'equipment fault. Allow extra travel time.',
      startedAt: new Date(now - 47 * DEMO_MINUTE).toISOString(),
      seenAt: new Date(now - 46 * DEMO_MINUTE).toISOString(),
      url: 'https://www.ptv.vic.gov.au/live-travel-updates/',
    },
    {
      id: -103, entity: 'disruption', ptvId: 302513, line: 11,
      title: 'Pakenham line suspended', status: 'current', type: 'Suspended',
      description: 'Trains are not running on the Pakenham line due to an '
        + 'equipment fault. Allow extra travel time.',
      startedAt: new Date(now - 44 * DEMO_MINUTE).toISOString(),
      seenAt: new Date(now - 43 * DEMO_MINUTE).toISOString(),
      url: 'https://www.ptv.vic.gov.au/live-travel-updates/',
    },
    {
      id: -102, entity: 'disruption', ptvId: 302488, line: 12,
      title: 'Buses replace trains Sunday', status: 'planned', type: 'Planned Works',
      description: 'Buses replace trains between Elsternwick and Sandringham from '
        + '8:30pm Friday to the last service Sunday for scheduled maintenance works.',
      startedAt: new Date(now + 26 * 60 * DEMO_MINUTE).toISOString(),
      seenAt: new Date(now - 9 * 60 * DEMO_MINUTE).toISOString(),
      url: 'https://www.ptv.vic.gov.au/live-travel-updates/',
    },
  ];
}

/**
 * The set-up both demos start from: three watched lines, three saved stations,
 * and a phone with alerts already on.
 *
 * Pakenham, Sandringham and Frankston because they are three different
 * colours, three different corridors and three different kinds of line on the
 * map — and because the seeded commute runs on the first of them, so the Lines
 * tab, the board and the Alerts tab are describing one person's trip rather
 * than three unrelated ones.
 *
 * Carnegie is the DEFAULT station, so the board opens on it. State Library is
 * the other end of the commute; Richmond is the interchange everybody in this
 * half of the city changes at, and it is there so the saved-stations list has
 * a row nobody's rule mentions.
 *
 * The device row is a FICTION and says so: token 'demo', which `isServerToken`
 * rejects by name. Nothing registered with APNs, nothing was sent to our
 * server, and `serverAlerts` is therefore false — the demo's Settings screen
 * shows alerts ON, which is the state worth showing, without a line of it
 * being true of any real phone.
 */
function seedRows(now) {
  subscriptions.value = [sub(-1, 11), sub(-2, 12), sub(-3, 6)];
  // The GENERATED store has a seed of its own — three rows called "Disruption
  // 1" with a routeId of 1 — and it is already in the ref by the time this
  // runs. Every branch below used to assign over it; the live branch does not,
  // so it is cleared here instead, once, for all of them.
  disruptions.value = [];
  stations.value = [
    { id: -1, entity: 'station', name: 'Carnegie', stopId: 1034, suburb: 'Carnegie', lines: '4,11', isDefault: true },
    { id: -2, entity: 'station', name: 'State Library', stopId: 1234, suburb: 'Melbourne', lines: '4,11,14', isDefault: false },
    { id: -3, entity: 'station', name: 'Richmond', stopId: 1162, suburb: 'Richmond', lines: '1,2,4,7,9,11,12', isDefault: false },
  ];
  devices.value = [{
    id: -1, entity: 'device', token: 'demo', platform: 'ios',
    pushEnabled: true, registeredAt: new Date(now - 4 * 24 * 60 * DEMO_MINUTE).toISOString(),
  }];
  // HOME, so the reroute screens have something to plan towards.
  //
  // Carnegie and twenty minutes, which is the case the whole feature was built
  // for: both lines through it are the Cranbourne and Pakenham pair, and East
  // Malvern on the Glen Waverley line is 1.4 km away — far enough that nobody
  // would think of it, close enough to walk. A demo with no place set would
  // show the card and the button nowhere, which is three screens of this
  // release invisible to anybody looking at it.
  //
  // Written straight onto the ref rather than through `setPlace`: the demo's
  // prefs are in memory, and a real phone's places must never be touched by
  // seeding a demo.
  places.value = readPlaces({ home: { stopId: 1034, walkMinutes: 20 }, work: null });
}

/**
 * The commute the demo is about: Carnegie to State Library, weekdays, 08:00 to
 * 09:30.
 *
 * Through `saveRule` rather than assigned, because a rule is not its fields —
 * it is its fields plus the PATH those fields resolve to, and the path is what
 * every later screen reads. Assigning a literal here would seed a rule that
 * looks right on the Rules screen and matches nothing, which is the exact
 * failure `saveRule` refuses to let a person create.
 *
 * `writeRules()` inside it is a write to the in-memory prefs, so this costs
 * the real app nothing.
 */
async function seedRule() {
  const { ok } = await saveRule({
    from: 1034,
    to: 1234,
    fromName: 'Carnegie',
    toName: 'State Library',
    start: '08:00',
    end: '09:30',
    days: [1, 2, 3, 4, 5],
    lead: DEFAULT_LEAD,
    active: true,
  });
  // The planner could not join the two ends — a bundle without the metropolitan
  // network, which is a test environment and not a shipped one. The demo is
  // still worth showing without the rule, so this is not fatal.
  return ok;
}

/**
 * Resolves when the demo has finished seeding itself; already resolved in the
 * real app.
 *
 * Exported for the suite, and for nothing else. The seed is a promise chain —
 * an IndexedDB read, then a dynamically imported journey planner resolving the
 * commute's path — and a test that asserted against it without waiting would
 * be asserting against whichever half had landed.
 */
export const demoReady = !isDemo.value ? Promise.resolve() : (async () => {
  await subscriptionsReady;
  // The seed resolves a commute over the city's network, which is a chunk of
  // its own now, and this starts at import — before the entry has awaited it.
  await loadNetwork(currentCity());
  await seedDemo();
})();

async function seedDemo() {
  // Never overwrite rows somebody actually created. The stores are in memory
  // in demo mode so this can no longer happen through storage — but a suite
  // that imported this file after adding rows would still see them, and the
  // guard costs one comparison.
  if (subscriptions.value.some((s) => Number(s.id) > 0)) return;

  const fixture = qaFixture();
  const now = Date.now();

  if (fixture === 'empty') {
    subscriptions.value = [];
    disruptions.value = [];
    stations.value = [];
    devices.value = [];
    lastRefreshedAt.value = new Date(now);
    return;
  }

  if (sampleData.value) {
    seedRows(now);
    disruptions.value = fixture === 'default' ? [] : demoDisruptions(now);
    // A mute, so the Settings row can actually be seen. It is a state that
    // only exists after somebody has pressed a button on a notification,
    // which is not something a screenshot or a review can reach — and an
    // interface nobody can look at is an interface nobody checked. The LIVE
    // demo does not seed it: a demo that says alerts are on and then says
    // they are muted until 3pm is two contradictory answers to the only
    // question the Settings screen is asked.
    pushMute.value = { until: new Date(now + 74 * DEMO_MINUTE).toISOString(), by: 'window' };
    lastRefreshedAt.value = new Date(now);
    return;
  }

  // Live. The ledger is deliberately NOT seeded: the first poll fires the
  // moment the watched lines land (the watcher below), and whatever PTV says
  // about these three lines right now is what the demo shows. A seeded
  // disruption would be a claim about the network that the next poll would
  // contradict on screen.
  seedRows(now);
  // The defaults every new install gets, so Settings shows the real screen
  // rather than an empty one. `saveQuietWindows` cleans and stores them the
  // ordinary way; the store is memory.
  saveQuietWindows(DEFAULT_QUIET_WINDOWS.map((w) => ({ ...w, days: [...w.days] })));
  await seedRule();
  // Nothing seeds `lateagain:stopsat:v1`. The board's destination chip is
  // meant to come from the rule, exactly as it does in the real app on the
  // morning after somebody writes one — see `readDestination` in
  // DeparturesPage. Seeding a remembered destination would hide the one
  // behaviour worth showing.
}

// A first poll once the rows are in — for the real app, and for the live demo,
// which is the real app with a seed in it. The sample demo is excluded: its
// data is the point, and a poll would replace it.
//
// The guard is a session flag rather than `lastRefreshedAt`, which now survives
// a relaunch: reading "we refreshed at 07:42" from storage and concluding this
// launch had already polled would mean an app that never polls again.
let polledThisSession = false;

if (!sampleData.value) {
  watch(subscriptions, (rows) => {
    if (!rows.length || polledThisSession) return;
    polledThisSession = true;
    // AFTER THE SEED, WHICH IS ONLY EVER A WAIT IN THE DEMO. The demo's rows
    // land the moment they are assigned, while the commute rule behind them is
    // still being resolved — the journey planner is imported dynamically. A
    // poll that got in first would find the rule missing, announce nothing,
    // and then never reconsider: the rows are on the ledger and stamped
    // `seenAt`, so every later poll treats them as already considered. The
    // symptom is an Alerts tab that is permanently empty by one turn of the
    // event loop. In the real app `demoReady` is an already-resolved promise.
    demoReady.then(resume);
  }, { immediate: true });
  watchForeground();
}
